import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  Archive,
  ArrowLeft,
  ArrowRight,
  BarChart3,
  BookOpen,
  Bot,
  Boxes,
  Bug,
  Camera,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleUserRound,
  Clock3,
  DollarSign,
  Download,
  ExternalLink,
  Eye,
  FolderOpen,
  Heart,
  Home,
  Images,
  HardDrive,
  KeyRound,
  Link2,
  LogIn,
  LogOut,
  LockKeyhole,
  Move,
  Orbit,
  Plus,
  RefreshCw,
  ScanLine,
  Search,
  Settings2,
  ShieldCheck,
  Shirt,
  Sparkles,
  Tag,
  Trash2,
  Upload,
  UsersRound,
  X,
} from 'lucide-react';
import { Store } from './mirror-store.js';
import { downloadState, readMirrorBackup } from './backup.js';
import { fileToOptimizedImage } from './image-compression.js';
import {
  changeMirrorPassword,
  clearAiSession,
  createMirrorAccount,
  createAdminUser,
  deleteAdminUser,
  getAiSession,
  getAdminMetrics,
  getAdminDevAttention,
  getAdminPasswordResetRequests,
  getAdminUsers,
  getMirrorFamilyProfiles,
  getMirrorParties,
  createMirrorParty,
  joinMirrorParty,
  leaveMirrorParty,
  replaceMirrorPartyMirror,
  linkMirrorFamily,
  lockAiSessionIdentity,
  loginToMirror,
  marketplaceSearchLinks,
  mergeAiSuggestion,
  refreshMirrorSession,
  reflectWitnessNote,
  recordMirrorActivity,
  scanMirrorItem,
  requestPasswordReset,
  selectMirrorFamilyProfile,
  uploadMirrorImage,
  updateAdminUserPlan,
} from './ai.js';
import { PrivateImage, clearPrivateMediaCache } from './private-media.jsx';
import { SyncConflict, SyncPill } from './SyncStatus.jsx';
import { useMirrorSync } from './use-mirror-sync.js';
import { formatCountdown, isCooldownFor, RateLimitNotice, useRateLimitCooldown } from './RateLimitNotice.jsx';
import {
  compactThreadLabel,
  getStorageUsage,
  getThreadUsage,
  MONTHLY_THREAD_SCALE,
  nextMonthlyResetLabel,
} from './usage.js';
import { AiZerepCredit, BrandMark, BUILD_VERSION, MirrorFooter, MirrorSplashLogo, MirrorWord, profileInitial } from './branding.jsx';
import RouteErrorBoundary from './RouteErrorBoundary.jsx';
import DesktopWorkspace, { DesktopInsightsView, DesktopIntakeModule } from './DesktopWorkspace.jsx';
import MirrorField from './MirrorField.jsx';
import ReflectiveToday from './ReflectiveToday.jsx';
import MirrorLens from './MirrorLens.jsx';
import SurroundingView from './SurroundingView.jsx';
import MirroredSchedule from './MirroredSchedule.jsx';
import QuickActions from './QuickActions.jsx';
import { SURROUNDING_BRANCHES, SURROUNDING_KIND_OPTIONS, SURROUNDING_THEMES, getSurroundingKind, normalizeSurroundingDetails } from './surrounding-config.js';
import { getWardrobeTags, matchesItemSearch } from './wardrobe-search.js';
import { isSockItem, laundryVitalityLabel } from './wardrobe-stats.js';
import { browserNotificationPermission, browserNotificationsSupported, requestBrowserNotificationPermission, showMirrorNotification } from './notifications.js';
import { getAchievementSummary, referralUrl } from './achievements.js';

const TRYON_CHUNK_RETRY_KEY = `mirror-tryon-chunk-retry:${BUILD_VERSION}`;
const DemoExperience = lazy(() => import('./DemoExperience.jsx'));
const GuidesView = lazy(() => import('./GuidesView.jsx'));
const ImportHub = lazy(() => import('./ImportHub.jsx'));
const TryOnStudio = lazy(() => import('./TryOnStudio.jsx').then(module => {
  try { sessionStorage.removeItem(TRYON_CHUNK_RETRY_KEY); } catch { /* Hardened browsers may deny session storage. */ }
  return module;
}).catch(error => {
  try {
    if (!sessionStorage.getItem(TRYON_CHUNK_RETRY_KEY)) {
      sessionStorage.setItem(TRYON_CHUNK_RETRY_KEY, '1');
      window.location.reload();
    }
  } catch { /* The error boundary below remains the recovery path. */ }
  throw error;
}));

const NAV = [
  { id: 'today', label: 'Today', icon: Home },
  { id: 'closet', label: 'Closet', icon: Shirt },
  { id: 'action', label: 'Add', icon: Plus, primary: true },
  { id: 'surrounding', label: 'Surroundings', icon: Boxes },
  { id: 'schedule', label: 'Schedule', icon: CalendarDays },
  { id: 'profile', label: 'Profile', icon: CircleUserRound },
];

const MOBILE_CLOSET_NAV = [
  { id: 'public-mirror', label: 'Public Mirror', icon: UsersRound },
  { id: 'field', label: 'Field', icon: Orbit },
  { id: 'intake', label: 'Unsorted Intake', icon: Archive },
  { id: 'ootd', label: 'OoTD', icon: Heart },
  { id: 'looks', label: 'Looks', icon: Images },
  { id: 'insights', label: 'Insights', icon: BarChart3 },
];

const PROFILE_SIGNAL_KEY = 'mirror_profile_signal';

function AccountGauge({
  icon: Icon,
  label,
  valueLabel,
  detail,
  note,
  mode,
  fillPercent,
  meterMax = 100,
  meterValue = 0,
  meterText,
}) {
  const level = mode === 'open'
    ? 'open'
    : mode === 'unavailable'
      ? 'unavailable'
      : fillPercent <= 0
        ? 'empty'
        : fillPercent <= 20
          ? 'low'
          : 'ready';
  const track = <span className="usage-gauge-track" aria-hidden="true"><span style={{ width: `${Math.min(100, Math.max(0, fillPercent))}%` }} /></span>;

  return (
    <article className={`usage-gauge ${level}`}>
      <div className="usage-gauge-label"><Icon aria-hidden="true" /><span>{label}</span></div>
      <strong>{valueLabel}</strong>
      <small>{detail}</small>
      {mode === 'finite' ? (
        <div
          className="usage-gauge-meter"
          role="meter"
          aria-label={label}
          aria-valuemin="0"
          aria-valuemax={meterMax}
          aria-valuenow={meterValue}
          aria-valuetext={meterText}
        >{track}</div>
      ) : <div className="usage-gauge-meter">{track}</div>}
      <p>{note}</p>
    </article>
  );
}

function hasWardrobeData(state) {
  const poses = Object.values(state?.mirror?.poseSlots || {}).some(slot => slot?.imageData || slot?.mediaId);
  const customSpace = (state?.collections || []).length > 1 || (state?.collections?.[0]?.name && state.collections[0].name !== 'My Space');
  return !!(customSpace || state?.locations?.length || state?.mirror?.outfits?.length || state?.mirror?.ootdHistory?.length || state?.mirror?.subjectImageData || state?.mirror?.subjectMediaId || poses);
}

function announceProfileChange() {
  localStorage.setItem(PROFILE_SIGNAL_KEY, `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  if (typeof BroadcastChannel === 'function') {
    const channel = new BroadcastChannel('mirror-profile');
    channel.postMessage({ type: 'profile-change' });
    channel.close();
  }
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, [query]);
  return matches;
}

function flattenState(state) {
  const all = [];
  for (const location of state.locations || []) {
    for (const zone of location.zones || []) {
      for (const item of zone.items || []) all.push({ ...item, location, zone });
    }
  }
  return all;
}

function getIntakeItems(state) {
  return flattenState(state).filter(entry => entry.location.type === 'intake_pile' || entry.location.name === 'Unsorted Intake');
}

function isIntakeLocation(location) {
  return location?.type === 'intake_pile' || location?.name === 'Unsorted Intake';
}

function collectionName(state, collectionId) {
  return state.collections?.find(collection => collection.id === collectionId)?.name || 'Space';
}

function findItemEntry(state, reference) {
  if (!reference) return null;
  return flattenState(state).find(entry => entry.id === reference.itemId && entry.location.id === reference.locationId && entry.zone.id === reference.zoneId) || null;
}

function ensureIntake() {
  const current = Store.getState();
  let location = current.locations.find(entry => entry.collectionId === current.activeCollectionId && isIntakeLocation(entry));
  if (!location) location = Store.addLocation('Unsorted Intake', 'intake_pile');
  let zone = Store.getLocation(location.id)?.zones?.find(entry => entry.name === 'New captures');
  if (!zone) zone = Store.addZone(location.id, 'New captures');
  return { locationId: location.id, zoneId: zone.id };
}

function SpaceSetupSheet({ open, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [vibe, setVibe] = useState('wardrobe');
  useEffect(() => { if (open) { setName(''); setVibe('wardrobe'); } }, [open]);
  if (!open) return null;
  const submit = event => {
    event.preventDefault();
    if (!name.trim()) return;
    onCreate(name.trim(), vibe);
  };
  return <div className="sheet-backdrop" role="presentation" onMouseDown={event => event.target === event.currentTarget && onClose()}>
    <form className="capture-sheet setup-sheet" role="dialog" aria-modal="true" aria-labelledby="space-sheet-title" onSubmit={submit}>
      <div className="sheet-handle" />
      <div className="sheet-title"><div><span>Make room</span><h2 id="space-sheet-title">Create an empty space.</h2></div><button type="button" onClick={onClose} aria-label="Close"><X /></button></div>
      <p>Start open. Add closets only when you are ready to place something.</p>
      <label className="setup-field"><span>Space name</span><input autoFocus value={name} onChange={event => setName(event.target.value)} maxLength="80" placeholder="e.g. Studio wardrobe" required /></label>
      <label className="setup-field"><span>Vibe</span><select value={vibe} onChange={event => setVibe(event.target.value)}><option value="wardrobe">Wardrobe</option><option value="home">Home</option><option value="resale">Resale</option><option value="travel">Travel</option></select></label>
      <button className="save-capture">Create empty space <ArrowRight /></button>
    </form>
  </div>;
}

function ClosetSetupSheet({ open, onClose, onCreate, spaceName = 'this space' }) {
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState('home');
  useEffect(() => { if (open) { setName(''); setPurpose('home'); } }, [open]);
  if (!open) return null;
  const submit = event => {
    event.preventDefault();
    if (!name.trim()) return;
    onCreate(name.trim(), purpose);
  };
  return <div className="sheet-backdrop" role="presentation" onMouseDown={event => event.target === event.currentTarget && onClose()}>
    <form className="capture-sheet setup-sheet" role="dialog" aria-modal="true" aria-labelledby="closet-sheet-title" onSubmit={submit}>
      <div className="sheet-handle" />
      <div className="sheet-title"><div><span>{spaceName}</span><h2 id="closet-sheet-title">Add a ready closet.</h2></div><button type="button" onClick={onClose} aria-label="Close"><X /></button></div>
      <p><MirrorWord /> adds a Drop-in section immediately, so your next piece already has somewhere to land.</p>
      <label className="setup-field"><span>Closet name</span><input autoFocus value={name} onChange={event => setName(event.target.value)} maxLength="80" placeholder="e.g. Main wardrobe" required /></label>
      <label className="setup-field"><span>Purpose</span><select value={purpose} onChange={event => setPurpose(event.target.value)}><option value="home">Home</option><option value="sell">Sell closet</option><option value="mixed">Mixed</option></select></label>
      <button className="save-capture">Add closet + Drop-in <ArrowRight /></button>
    </form>
  </div>;
}

function LocationCleanupRow({ location, state, refresh }) {
  const [locationName, setLocationName] = useState(location.name || '');
  const [zoneNames, setZoneNames] = useState(() => Object.fromEntries((location.zones || []).map(zone => [zone.id, zone.name || ''])));
  useEffect(() => {
    setLocationName(location.name || '');
    setZoneNames(Object.fromEntries((location.zones || []).map(zone => [zone.id, zone.name || ''])));
  }, [location.id, location.name, location.zones?.map(zone => `${zone.id}:${zone.name}`).join('|')]);
  const save = event => {
    event.preventDefault();
    const nextLocationName = locationName.trim();
    if (nextLocationName && nextLocationName !== location.name) Store.updateLocationName(location.id, nextLocationName);
    (location.zones || []).forEach(zone => {
      const nextZoneName = String(zoneNames[zone.id] || '').trim();
      if (nextZoneName && nextZoneName !== zone.name) Store.updateZoneName(location.id, zone.id, nextZoneName);
    });
    refresh();
  };
  const removeLocation = () => {
    const count = (location.zones || []).reduce((total, zone) => total + (zone.items || []).length, 0);
    if (!window.confirm(`Delete ${location.name}${count ? ` and its ${count} saved ${count === 1 ? 'record' : 'records'}` : ''}? This cannot be undone.`)) return;
    Store.deleteLocation(location.id);
    refresh();
  };
  const removeZone = zone => {
    const count = zone.items?.length || 0;
    if (!window.confirm(`Delete ${zone.name}${count ? ` and its ${count} saved ${count === 1 ? 'record' : 'records'}` : ''}? This cannot be undone.`)) return;
    Store.deleteZone(location.id, zone.id);
    refresh();
  };
  return <form className="location-cleanup-row" onSubmit={save}>
    <div className="location-cleanup-row-heading"><div><span>{location.type === 'surrounding' ? 'Surrounding location' : 'Closet location'}</span><strong>{state.collections?.find(collection => collection.id === location.collectionId)?.name || 'Space'}</strong></div><div><button type="submit">Save names</button><button type="button" className="location-delete-action" onClick={removeLocation}><Trash2 /> Delete</button></div></div>
    <label><span>Location</span><input value={locationName} onChange={event => setLocationName(event.target.value)} /></label>
    <div className="location-zone-editor">{(location.zones || []).map(zone => <div className="location-zone-field" key={zone.id}><label><span>Section</span><input value={zoneNames[zone.id] || ''} onChange={event => setZoneNames(current => ({ ...current, [zone.id]: event.target.value }))} /></label><button type="button" onClick={() => removeZone(zone)} aria-label={`Delete ${zone.name}`}><Trash2 /></button></div>)}</div>
  </form>;
}

function LocationCleanupPanel({ state, refresh }) {
  const locations = (state.locations || []).filter(location => !isIntakeLocation(location));
  return <section className="location-cleanup-panel" aria-labelledby="location-cleanup-title">
    <div className="location-cleanup-heading"><div><span>Location map</span><h2 id="location-cleanup-title">Name the homes you already have.</h2></div><FolderOpen /></div>
    <p className="location-cleanup-lede"><strong>Intake</strong> is the temporary capture queue. <strong>Drop-in</strong> is only the starter section created inside a new closet. It is safe to rename it to Everyday, Middle drawer, Shoes, or whatever makes sense to you.</p>
    {locations.length ? <div className="location-cleanup-list">{locations.map(location => <LocationCleanupRow key={location.id} location={location} state={state} refresh={refresh} />)}</div> : <p className="location-cleanup-empty">Create a closet and its first section, then its real names can live here.</p>}
  </section>;
}

function ClosetView({ state, refresh, openCapture, onOpenItem, sync, onAbortSync, onResumeSync, initialFilter = 'all', initialLocation = 'all', user }) {
  const [query, setQuery] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('all');
  const [selectedZoneId, setSelectedZoneId] = useState('all');
  const [intentFilter, setIntentFilter] = useState('all');
  const [spaceSheetOpen, setSpaceSheetOpen] = useState(false);
  const [closetSheetOpen, setClosetSheetOpen] = useState(false);
  const [locationCleanupOpen, setLocationCleanupOpen] = useState(false);
  const [groupEdit, setGroupEdit] = useState(false);
  const activeCollection = state.collections?.find(collection => collection.id === state.activeCollectionId) || state.collections?.[0];
  const locations = useMemo(
    () => (state.locations || []).filter(location => location.collectionId === activeCollection?.id),
    [activeCollection?.id, state.locations],
  );
  const selectedLocation = locations.find(location => location.id === selectedLocationId) || null;
  const allItems = useMemo(
    () => flattenState(state).filter(item => item.location.collectionId === activeCollection?.id),
    [activeCollection?.id, state],
  );
  const wardrobeTags = useMemo(() => getWardrobeTags(allItems), [allItems]);
  const intakeItems = allItems.filter(item => isIntakeLocation(item.location));
  const laundryItems = allItems.filter(item => item.wearStatus === 'laundry');
  const sockItems = useMemo(() => allItems.filter(isSockItem), [allItems]);
  const publicMirrorItems = useMemo(() => allItems.filter(item => item.publicMirror && !isIntakeLocation(item.location)), [allItems]);
  const sockPairs = Math.max(0, Math.min(200, Number(state.preferences?.sockPairsAvailable ?? 12)));
  const sockVitality = laundryVitalityLabel({ sockPairs, laundryCount: laundryItems.length });

  useEffect(() => {
    if (selectedLocationId === 'public-mirror') return;
    if (selectedLocationId !== 'all' && !locations.some(location => location.id === selectedLocationId)) {
      setSelectedLocationId('all');
      setSelectedZoneId('all');
    }
  }, [locations, selectedLocationId]);

  useEffect(() => {
    setIntentFilter(initialFilter);
  }, [initialFilter]);

  useEffect(() => {
    setSelectedLocationId(initialLocation);
    setSelectedZoneId('all');
  }, [initialLocation]);

  useEffect(() => {
    if (tagFilter && !wardrobeTags.includes(tagFilter)) setTagFilter('');
  }, [tagFilter, wardrobeTags]);

  const filtered = useMemo(() => {
    return allItems.filter(item => {
      if (selectedLocationId === 'public-mirror' && !item.publicMirror) return false;
      if (selectedLocationId === 'intake' && !isIntakeLocation(item.location)) return false;
      if (!['all', 'intake'].includes(selectedLocationId) && item.location.id !== selectedLocationId) return false;
      if (selectedZoneId !== 'all' && item.zone.id !== selectedZoneId) return false;
      if (intentFilter === 'wear' && !item.intent?.wear) return false;
      if (intentFilter === 'sell' && !item.intent?.sell) return false;
      if (intentFilter === 'work' && !item.workwear) return false;
      if (intentFilter === 'laundry' && item.wearStatus !== 'laundry') return false;
      return matchesItemSearch(item, query, tagFilter);
    });
  }, [allItems, intentFilter, query, selectedLocationId, selectedZoneId, tagFilter]);

  const chooseCollection = event => {
    Store.setActiveCollection(event.target.value);
    setSelectedLocationId('all');
    setSelectedZoneId('all');
    setGroupEdit(false);
    setLocationCleanupOpen(false);
    refresh();
  };
  const chooseLocation = id => {
    setSelectedLocationId(id);
    setSelectedZoneId('all');
  };
  const setPurpose = purpose => {
    if (!selectedLocation) return;
    Store.updateLocationPurpose(selectedLocation.id, purpose);
    refresh();
  };
  const locationCount = location => allItems.filter(item => item.location.id === location.id).length;
  const realClosets = locations.filter(location => !isIntakeLocation(location));
  const showBrowse = realClosets.length || intakeItems.length || publicMirrorItems.length || intentFilter === 'work' || intentFilter === 'laundry' || query.trim();
  const refreshLaundry = () => {
    Store.setItemsWearStatus(laundryItems.map(item => item.id), 'clean');
    refresh();
  };
  const updateSockPairs = event => {
    const value = Math.max(0, Math.min(200, Number(event.target.value) || 0));
    Store.updatePreferences({ sockPairsAvailable: value });
    refresh();
  };
  const createSpace = (name, vibe) => {
    Store.addCollection(name, vibe);
    setSelectedLocationId('all');
    setSelectedZoneId('all');
    setSpaceSheetOpen(false);
    refresh();
  };
  const deleteActiveSpace = () => {
    if (!activeCollection || state.collections.length <= 1) return;
    const count = allItems.length;
    if (!window.confirm(`Delete ${activeCollection.name}${count ? ` and its ${count} saved ${count === 1 ? 'record' : 'records'}` : ''}? This cannot be undone.`)) return;
    Store.deleteCollection(activeCollection.id);
    setSelectedLocationId('all');
    setSelectedZoneId('all');
    setGroupEdit(false);
    setLocationCleanupOpen(false);
    refresh();
  };
  const createCloset = (name, purpose) => {
    const location = Store.addLocation(name, 'closet', activeCollection?.id);
    Store.updateLocationPurpose(location.id, purpose);
    Store.addZone(location.id, 'Drop-in');
    setSelectedLocationId(location.id);
    setSelectedZoneId('all');
    setClosetSheetOpen(false);
    refresh();
  };

  return (
    <main className="view closet-view">
      <div className="page-title-row">
        <div><span>Spaces</span><h1>Your closet</h1></div>
        <button className="avatar" aria-label="Profile"><span>{profileInitial(user)}</span></button>
      </div>
      <SyncPill sync={sync} onAbort={onAbortSync} onResume={onResumeSync} />

      <section className={`closet-control-card ${groupEdit ? 'is-group-editing' : ''}`} aria-label="Closet controls">
        <div className="space-select-row">
          <label className="space-select">
            <FolderOpen />
            <span><small>Current space</small><select value={activeCollection?.id || ''} onChange={chooseCollection}>{(state.collections || []).map(collection => <option value={collection.id} key={collection.id}>{collection.name}</option>)}</select></span>
            <ChevronDown />
          </label>
          <button className="create-space-button" onClick={() => setSpaceSheetOpen(true)}><Plus /><span>Create space</span></button>
          <button className={`group-edit-toggle ${groupEdit ? 'active' : ''}`} aria-pressed={groupEdit} onClick={() => { setGroupEdit(value => !value); setLocationCleanupOpen(false); }}><Settings2 /><span>{groupEdit ? 'Done editing' : 'Group edit'}</span></button>
        </div>

        {groupEdit ? <section className="closet-group-edit-panel" aria-label="Space and closet group settings">
          <div><Settings2 /><span><strong>Group editing is on</strong><small>Renaming and deletion stay together here so normal browsing cannot remove a space by accident.</small></span></div>
          <div><button type="button" onClick={() => setLocationCleanupOpen(value => !value)}>{locationCleanupOpen ? 'Close location map' : 'Rename locations & sections'} <ChevronRight /></button>{state.collections.length > 1 ? <button type="button" className="danger" onClick={deleteActiveSpace}><Trash2 /> Delete current space</button> : null}</div>
          {locationCleanupOpen ? <LocationCleanupPanel state={state} refresh={refresh} /> : null}
        </section> : null}

        {realClosets.length || intakeItems.length ? <section className="closet-browser" aria-labelledby="closets-label">
          <div className="organizer-label"><span id="closets-label">Closets</span><small>Choose where to look</small></div>
          <div className="location-rail">
            <button className={selectedLocationId === 'all' ? 'active' : ''} onClick={() => chooseLocation('all')}><strong>All</strong><small>{allItems.length} pieces</small></button>
            <button className={selectedLocationId === 'intake' ? 'active intake' : 'intake'} onClick={() => chooseLocation('intake')}><strong>Intake</strong><small>{intakeItems.length} to sort</small></button>
            <button className={selectedLocationId === 'public-mirror' ? 'active public-mirror' : 'public-mirror'} onClick={() => chooseLocation('public-mirror')}><strong>Public Mirror</strong><small>{publicMirrorItems.length} shared pieces</small></button>
            {locations.filter(location => !isIntakeLocation(location)).map(location => <button className={selectedLocationId === location.id ? 'active' : ''} onClick={() => chooseLocation(location.id)} key={location.id}><strong>{location.name}</strong><small>{locationCount(location)} · {location.purpose || 'home'}</small></button>)}
          </div>
        </section> : <section className="space-ready"><FolderOpen /><span>Empty space</span><h2>This space is ready.</h2><p>No closets yet. Keep it open, or add one when you have something to place.</p><div><button onClick={() => setClosetSheetOpen(true)}>Add a closet</button><button onClick={openCapture}>Capture to this space</button></div></section>}

        {selectedLocation ? <section className="location-controls">
          <div className="organizer-label"><span>Closet purpose</span><small>How this space serves you</small></div>
          <div className="purpose-toggle" role="group" aria-label="Closet purpose">
            {['home', 'sell', 'mixed'].map(purpose => <button aria-pressed={selectedLocation.purpose === purpose} className={selectedLocation.purpose === purpose ? 'active' : ''} onClick={() => setPurpose(purpose)} key={purpose}>{purpose === 'home' ? 'Home' : purpose === 'sell' ? 'Sell closet' : 'Mixed'}</button>)}
          </div>
          {selectedLocation.zones?.length > 1 ? <div className="zone-rail"><button className={selectedZoneId === 'all' ? 'active' : ''} onClick={() => setSelectedZoneId('all')}>All sections</button>{selectedLocation.zones.map(zone => <button className={selectedZoneId === zone.id ? 'active' : ''} onClick={() => setSelectedZoneId(zone.id)} key={zone.id}>{zone.name}</button>)}</div> : null}
        </section> : null}

        {selectedLocationId === 'intake' && intakeItems.length ? <button className="cleanup-intake" onClick={() => onOpenItem(intakeItems[0])}><Archive /><span><strong>Clean up intake</strong><small>Choose Wear, Sell, then its closet</small></span><ArrowRight /></button> : null}

        {showBrowse ? <>
          <label className="search-field"><Search /><input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search pieces, brands, colors" /></label>
          <div className="filter-line intent-filter">
            <button className={intentFilter === 'all' ? 'active' : ''} onClick={() => setIntentFilter('all')}>All {filtered.length}</button>
            <button className={intentFilter === 'wear' ? 'active' : ''} onClick={() => setIntentFilter('wear')}><Heart /> Wear</button>
            <button className={intentFilter === 'sell' ? 'active' : ''} onClick={() => setIntentFilter('sell')}><Tag /> Sell</button>
            <button className={intentFilter === 'work' ? 'active' : ''} onClick={() => setIntentFilter('work')}><Shirt /> Work clothes</button>
            <button className={intentFilter === 'laundry' ? 'active' : ''} onClick={() => setIntentFilter('laundry')}><RefreshCw /> Laundry {laundryItems.length ? laundryItems.length : ''}</button>
          </div>
          {wardrobeTags.length ? <div className="tag-filter-line" aria-label="Filter by item tag"><span>Tags</span><button className={!tagFilter ? 'active' : ''} onClick={() => setTagFilter('')}>All</button>{wardrobeTags.map(tag => <button className={tagFilter === tag ? 'active' : ''} onClick={() => setTagFilter(tag)} key={tag}>{tag}</button>)}</div> : null}
          {laundryItems.length ? <button className="laundry-refresh" onClick={refreshLaundry}><RefreshCw /> Mark all laundry clean</button> : null}
        </> : null}
      </section>

      {showBrowse ? filtered.length ? (
        <div className="closet-grid">
          {filtered.map(item => (
            <button className={`closet-item ${item.wearStatus && item.wearStatus !== 'clean' ? 'is-worn' : ''}`} key={item.id} onClick={() => onOpenItem(item)}>
              <span className="closet-image"><PrivateImage source={item.imageData} mediaId={item.imageMediaId} fallback="./assets/silver-knit.webp" alt="" loading="lazy" /></span>
              <span className="closet-item-title">{item.label || 'Untitled piece'}</span>
              <span className="closet-item-place">{item.location.name} · {item.zone.name}</span>
              <span className="item-flags">{item.intent?.wear ? <i><Heart /> Wear</i> : null}{item.intent?.sell ? <i><Tag /> Sell</i> : null}{item.publicMirror ? <i><UsersRound /> Public Mirror</i> : null}{item.workwear ? <i><Shirt /> Work clothes</i> : null}{item.wearStatus === 'laundry' ? <i><RefreshCw /> Laundry</i> : item.wearStatus === 'worn' ? <i><Check /> Worn</i> : null}{item.aiScanStatus === 'queued' ? <i><Clock3 /> Queued</i> : item.aiScanStatus === 'scanning' ? <i><ScanLine /> Scanning</i> : null}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="empty-state"><Shirt /><h2>Nothing in this view yet.</h2><p>Try another closet or capture a new piece.</p><button onClick={openCapture}>Capture an item</button></div>
      ) : null}

      <details className="closet-support-details"><summary><span><small>Wardrobe support</small><strong>Sock vitality and laundry truth</strong></span><ChevronDown /></summary><div>
        <section className="sock-vitality-panel" aria-labelledby="sock-vitality-title">
          <div className="sock-vitality-copy"><span>Quiet wardrobe truth</span><h2 id="sock-vitality-title">Sock drawer</h2><strong>{sockPairs} pairs available</strong><p>{sockVitality}. Socks are the honest laundry gauge: bottoms till necessity.</p></div>
          <label className="sock-pairs-field"><span>Pairs</span><input type="number" min="0" max="200" value={sockPairs} onChange={updateSockPairs} aria-label="Sock pairs available" /></label>
          <small>{laundryItems.length} clothing {laundryItems.length === 1 ? 'item' : 'items'} in laundry · {sockItems.length} sock {sockItems.length === 1 ? 'record' : 'records'} saved</small>
        </section>
      </div></details>
      <SpaceSetupSheet open={spaceSheetOpen} onClose={() => setSpaceSheetOpen(false)} onCreate={createSpace} />
      <ClosetSetupSheet open={closetSheetOpen} onClose={() => setClosetSheetOpen(false)} onCreate={createCloset} spaceName={activeCollection?.name} />
    </main>
  );
}

function parseEstimateRange(value) {
  const numbers = String(value || '').replace(/,/g, '').match(/\d+(?:\.\d+)?/g)?.map(Number).filter(Number.isFinite) || [];
  if (!numbers.length) return null;
  return { min: Math.min(...numbers), max: Math.max(...numbers) };
}

function formatUsd(value) {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

function formatApiCost(microusd) {
  const usd = Math.max(0, Number(microusd || 0)) / 1_000_000;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: usd > 0 && usd < 1 ? 4 : 2,
    maximumFractionDigits: usd > 0 && usd < 1 ? 6 : 2,
  }).format(usd);
}

function sellableEstimateLabel(item) {
  const market = item.aiMarket || {};
  return market.aftermarketValue || market.currentMarketValue || '';
}

function thriftCategory(item) {
  return item.location?.type === 'surrounding' ? getSurroundingKind(item) : item.details?.category?.trim() || 'Other';
}

function restrictedSaleReason(item) {
  const haystack = [item.label, item.details?.category, item.details?.care, item.ocrText, ...(item.tags || []), item.surrounding?.kind].filter(Boolean).join(' ').toLowerCase();
  if (/medicat|medicine|prescription|controlled substance|illegal drug|narcotic/.test(haystack)) return 'Medication / controlled item';
  if (/weapon|firearm|ammunition|explosive/.test(haystack)) return 'Restricted weapon item';
  if (/alcohol|tobacco|cigarette|vape|nicotine/.test(haystack)) return 'Age-restricted item';
  if (/food|grocery|milk|supplement/.test(haystack)) return 'Consumable item';
  return '';
}

function ThriftView({ items, onOpenItem, refresh }) {
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const sellItems = useMemo(() => items.filter(item => item.intent?.sell), [items]);
  const readyItems = useMemo(() => sellItems.filter(item => !restrictedSaleReason(item)), [sellItems]);
  const approvalItems = useMemo(() => sellItems.filter(item => restrictedSaleReason(item)), [sellItems]);
  const categories = useMemo(() => [...new Set(readyItems.map(thriftCategory))].sort((a, b) => a.localeCompare(b)), [readyItems]);
  const tags = useMemo(() => getWardrobeTags(readyItems), [readyItems]);
  const filtered = useMemo(() => readyItems.filter(item => {
    const categoryMatches = categoryFilter === 'all' || thriftCategory(item) === categoryFilter;
    return categoryMatches && matchesItemSearch(item, query, tagFilter);
  }), [categoryFilter, query, readyItems, tagFilter]);
  const ranges = readyItems.map(item => parseEstimateRange(sellableEstimateLabel(item))).filter(Boolean);
  const total = ranges.reduce((sum, range) => ({ min: sum.min + range.min, max: sum.max + range.max }), { min: 0, max: 0 });
  const totalLabel = ranges.length ? total.min === total.max ? formatUsd(total.min) : `${formatUsd(total.min)}–${formatUsd(total.max)}` : 'Awaiting estimates';
  useEffect(() => { if (categoryFilter !== 'all' && !categories.includes(categoryFilter)) setCategoryFilter('all'); }, [categories, categoryFilter]);
  useEffect(() => { if (tagFilter && !tags.includes(tagFilter)) setTagFilter(''); }, [tagFilter, tags]);
  const savePrice = (item, price) => {
    Store.updateItem(item.location.id, item.zone.id, item.id, { listing: { ...(item.listing || {}), price, status: item.listing?.status === 'none' ? 'draft' : item.listing?.status || 'draft' } });
    refresh();
  };
  return <main className="view thrift-view">
    <div className="page-title-row"><div><span>Reverse garage sale prep</span><h1>Thrift.</h1></div><Tag /></div>
    <p className="page-lede">Everything you marked for Sell, gathered in one place. Set a price, open the record, and decide what earns a second life.</p>
    <section className="thrift-summary"><div><span>Ready to prepare</span><strong>{readyItems.length} {readyItems.length === 1 ? 'record' : 'records'}</strong></div><div><span>Estimated lot value</span><strong>{totalLabel}</strong></div><div><span>Needs approval</span><strong>{approvalItems.length}</strong></div></section>
    <label className="search-field"><Search /><input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search thrift, tags, colors, genre" /></label>
    {categories.length ? <div className="sellable-category-filter thrift-filter" role="group" aria-label="Filter thrift records by type"><button className={categoryFilter === 'all' ? 'active' : ''} onClick={() => setCategoryFilter('all')}>All</button>{categories.map(category => <button className={categoryFilter === category ? 'active' : ''} onClick={() => setCategoryFilter(category)} key={category}>{category}</button>)}</div> : null}
    {tags.length ? <div className="tag-filter-line" aria-label="Filter thrift records by tag"><span>Tags</span><button className={!tagFilter ? 'active' : ''} onClick={() => setTagFilter('')}>All</button>{tags.map(tag => <button className={tagFilter === tag ? 'active' : ''} onClick={() => setTagFilter(tag)} key={tag}>{tag}</button>)}</div> : null}
    <section className="thrift-list" aria-labelledby="thrift-ready-title"><div className="thrift-section-heading"><div><span>Catch-all sale list</span><h2 id="thrift-ready-title">Ready to price</h2></div><span>{filtered.length} shown</span></div>{filtered.length ? filtered.map(item => { const estimate = sellableEstimateLabel(item); return <article className="thrift-row" key={`${item.location.id}:${item.zone.id}:${item.id}`}><button className="thrift-row-open" onClick={() => onOpenItem(item)}><PrivateImage source={item.imageData} mediaId={item.imageMediaId} fallback="./assets/silver-knit.webp" alt="" /><span><strong>{item.label || 'Untitled record'}</strong><small>{thriftCategory(item)} · {estimate || 'No AI estimate yet'}</small></span><ArrowRight /></button><label className="thrift-price"><span>Price</span><input inputMode="decimal" value={item.listing?.price || ''} onChange={event => savePrice(item, event.target.value)} placeholder="—" aria-label={`Price for ${item.label || 'record'}`} /></label></article>; }) : <div className="thrift-empty"><Tag /><h2>Nothing is marked for sale yet.</h2><p>Use Sell list on any clothing or Surrounding record to bring it into this catch-all.</p></div>}</section>
    {approvalItems.length ? <section className="thrift-approval"><div className="thrift-section-heading"><div><span>Hold for review</span><h2>Needs approval</h2></div><span>Not included in lot value</span></div>{approvalItems.map(item => <button className="thrift-approval-row" key={`${item.location.id}:${item.zone.id}:${item.id}`} onClick={() => onOpenItem(item)}><span><strong>{item.label || 'Untitled record'}</strong><small>{restrictedSaleReason(item)}</small></span><ArrowRight /></button>)}<p>Medication, restricted, age-limited, and consumable records stay out of the sale total until you confirm they are appropriate to list.</p></section> : null}
  </main>;
}

function formatActivityDate(value) {
  if (!value) return 'No recent activity';
  try { return new Date(`${value.replace(' ', 'T')}Z`).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }); } catch { return value; }
}

function AdminMetricsView({ onBack, onOpenAccounts, onOpenDevAttention, adminName }) {
  const [days, setDays] = useState(30);
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setError('');
    getAdminMetrics(days).then(next => active && setMetrics(next)).catch(nextError => active && setError(nextError.message || 'Metrics are unavailable.'));
    return () => { active = false; };
  }, [days]);
  const maxHour = Math.max(1, ...(metrics?.byHour || []).map(row => row.events));
  const maxView = Math.max(1, ...(metrics?.byView || []).map(row => row.events));
  const maxAiUserCost = Math.max(1, ...(metrics?.aiByUser || []).map(row => row.costMicrousd));
  const topAiUser = metrics?.aiByUser?.[0] || null;
  return <main className="view admin-metrics-view">
    <header className="admin-metrics-header"><button onClick={onBack}><ArrowLeft /> Profile</button><div><span>{adminName} · private admin</span><h1>Activity metrics</h1></div><Activity /></header>
    <div className="admin-workspace-tabs" role="tablist" aria-label="Admin workspace"><button className="active" role="tab" aria-selected="true">Activity</button><button role="tab" aria-selected="false" onClick={onOpenAccounts}>Accounts</button><button role="tab" aria-selected="false" onClick={onOpenDevAttention}>Dev attention</button></div>
    <div className="metrics-range" role="group" aria-label="Metrics range">{[7, 30, 90].map(value => <button className={days === value ? 'active' : ''} onClick={() => setDays(value)} key={value}>{value}d</button>)}</div>
    {error ? <div className="metrics-error" role="alert">{error}</div> : metrics ? <>
      <section className="metrics-card-grid" aria-label="Usage summary">
        <article><span>Total views</span><strong>{metrics.summary.events.toLocaleString()}</strong><small>last {metrics.range.days} days</small></article>
        <article><span>Active users</span><strong>{metrics.summary.users.toLocaleString()}</strong><small>{metrics.summary.active24h} in 24h · {metrics.summary.active7d} in 7d</small></article>
        <article><span>Sessions</span><strong>{metrics.summary.sessions.toLocaleString()}</strong><small>distinct test sessions</small></article>
      </section>
      <section className="metrics-section"><div className="metrics-section-heading"><div><span>Where people go</span><h2>Views</h2></div><BarChart3 /></div><div className="metric-bars">{metrics.byView.length ? metrics.byView.map(row => <div key={row.view}><span>{row.view}</span><div><i style={{ width: `${Math.max(4, row.events / maxView * 100)}%` }} /></div><b>{row.events}</b></div>) : <p>No activity yet.</p>}</div></section>
      <section className="metrics-section"><div className="metrics-section-heading"><div><span>UTC · busiest windows</span><h2>Hot hours</h2></div><Clock3 /></div><div className="hour-bars">{metrics.byHour.map(row => <div className="hour-bar" title={`${String(row.hour).padStart(2, '0')}:00 UTC · ${row.events} views`} key={row.hour}><i style={{ height: `${row.events ? Math.max(8, row.events / maxHour * 100) : 3}%` }} /><small>{row.hour % 3 === 0 ? String(row.hour).padStart(2, '0') : ''}</small></div>)}</div></section>
      <section className="metrics-section ai-cost-metrics"><div className="metrics-section-heading"><div><span>OpenAI · settled usage</span><h2>Recorded API cost</h2></div><DollarSign /></div>
        <div className="ai-cost-summary" aria-label="API cost summary">
          <article><span>Selected period</span><strong>{formatApiCost(metrics.aiSummary?.costMicrousd)}</strong><small>last {metrics.range.days} days</small></article>
          <article><span>All time</span><strong>{formatApiCost(metrics.aiSummary?.allTimeCostMicrousd)}</strong><small>{Number(metrics.aiSummary?.allTimeActions || 0).toLocaleString()} settled actions</small></article>
          <article><span>AI actions</span><strong>{Number(metrics.aiSummary?.actions || 0).toLocaleString()}</strong><small>{Number(metrics.aiSummary?.inputTokens || 0).toLocaleString()} in · {Number(metrics.aiSummary?.outputTokens || 0).toLocaleString()} out</small></article>
          <article><span>Highest user</span><strong>{topAiUser ? topAiUser.displayName : '—'}</strong><small>{topAiUser ? `${formatApiCost(topAiUser.costMicrousd)} · ${topAiUser.actions} actions` : 'No recorded usage'}</small></article>
        </div>
        <p className="ai-cost-caveat">Costs come from successful provider response usage multiplied by the configured model rates. They are the app’s recorded API cost and may differ from invoice credits, taxes, or later provider adjustments.</p>
        <div className="ai-cost-layout">
          <section className="ai-user-ranking" aria-labelledby="ai-user-ranking-title"><div><span>Ranked by recorded cost</span><h3 id="ai-user-ranking-title">Who uses the most</h3></div>{metrics.aiByUser?.length ? <div>{metrics.aiByUser.map((user, index) => <article key={user.id}><b>{index + 1}</b><span className="profile-medallion">{profileInitial(user)}</span><div><strong>{user.displayName}</strong><small>@{user.username} · {user.scans} scans · {user.renders} try-ons</small><i><span style={{ width: `${Math.max(3, user.costMicrousd / maxAiUserCost * 100)}%` }} /></i></div><aside><strong>{formatApiCost(user.costMicrousd)}</strong><small>{user.actions} actions</small></aside></article>)}</div> : <p>No AI actions recorded in this period.</p>}</section>
          <div className="ai-cost-breakdowns">
            <section><div><span>By operation</span><h3>Cost centers</h3></div><div className="ai-metric-list">{metrics.aiUsage?.length ? metrics.aiUsage.map(row => <div key={row.endpoint}><strong>{row.endpoint}</strong><span>{formatApiCost(row.costMicrousd)}</span><small>{row.actions} actions</small></div>) : <p>No AI actions recorded yet.</p>}</div></section>
            <section><div><span>By provider model</span><h3>Models</h3></div><div className="ai-metric-list">{metrics.aiByModel?.length ? metrics.aiByModel.map(row => <div key={row.model}><strong>{row.model}</strong><span>{formatApiCost(row.costMicrousd)}</span><small>{row.actions} actions</small></div>) : <p>No model usage recorded yet.</p>}</div></section>
          </div>
        </div>
      </section>
      <section className="metrics-section referral-metrics" aria-labelledby="referral-metrics-title"><div className="metrics-section-heading"><div><span>Explicit invite attribution</span><h2 id="referral-metrics-title">Referral activity</h2></div><Link2 /></div><div className="metrics-card-grid"><article><span>Referred accounts</span><strong>{Number(metrics.referrals?.summary?.referredUsers || 0).toLocaleString()}</strong><small>last {metrics.range.days} days</small></article><article><span>Active referrers</span><strong>{Number(metrics.referrals?.summary?.referrers || 0).toLocaleString()}</strong><small>users who brought someone in</small></article><article><span>Total referrals</span><strong>{Number(metrics.referrals?.summary?.referrals || 0).toLocaleString()}</strong><small>one attribution per account</small></article></div>{metrics.referrals?.byUser?.length ? <div className="referral-ranking">{metrics.referrals.byUser.map((user, index) => <article key={user.id}><b>{index + 1}</b><span className="profile-medallion">{profileInitial(user)}</span><div><strong>{user.displayName}</strong><small>@{user.username} · last referral {formatActivityDate(user.lastReferralAt)}</small></div><em>{user.referrals}</em></article>)}</div> : <p>No referral activity recorded in this period.</p>}</section>
      <section className="metrics-section recent-users-metrics"><div className="metrics-section-heading"><div><span>Latest active accounts</span><h2>Recent users</h2></div><UsersRound /></div>{metrics.recentUsers.length ? <div>{metrics.recentUsers.map(user => <article key={user.id}><span className="profile-medallion">{profileInitial(user)}</span><div><strong>{user.displayName}</strong><small>@{user.username} · {user.lastView} · {user.events} views</small></div><time>{formatActivityDate(user.lastSeenAt)}</time></article>)}</div> : <p>No signed-in activity recorded yet.</p>}</section>
    </> : <div className="metrics-loading">Loading activity…</div>}
  </main>;
}

const ADMIN_TIER_OPTIONS = [
  { value: 'premium', label: 'Premium' },
  { value: 'verified', label: 'Verified User' },
  { value: 'trial', label: 'Trial' },
];
const ADMIN_ACCOUNT_TIER_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  ...ADMIN_TIER_OPTIONS,
];

function formatAccountBytes(value) {
  const bytes = Number(value || 0);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
}

function AdminAccountsView({ onBack, onOpenActivity, onOpenDevAttention, adminName }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ username: '', displayName: '', password: '', tier: 'trial' });
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadUsers = useCallback(async () => {
    const payload = await getAdminUsers();
    setUsers(payload.users || []);
  }, []);

  useEffect(() => {
    loadUsers().catch(nextError => setError(nextError.message || 'Accounts are unavailable.'));
  }, [loadUsers]);

  const createAccount = async event => {
    event.preventDefault();
    setBusy(true);
    setMessage('');
    setError('');
    try {
      await createAdminUser(form);
      setForm({ username: '', displayName: '', password: '', tier: 'trial' });
      await loadUsers();
      setMessage('Account created.');
    } catch (nextError) {
      setError(nextError.message || 'Could not create account.');
    } finally {
      setBusy(false);
    }
  };

  const changeTier = async (userId, tier) => {
    setBusy(true);
    setMessage('');
    setError('');
    try {
      await updateAdminUserPlan(userId, { tier });
      await loadUsers();
      setMessage('Tier updated.');
    } catch (nextError) {
      setError(nextError.message || 'Could not update tier.');
    } finally {
      setBusy(false);
    }
  };

  const deleteAccount = async user => {
    if (user.role === 'admin' || user.role === 'owner') return;
    const confirmed = window.confirm(`Permanently delete @${user.username}? This removes their wardrobe, private photos, AI history, and sign-in. This cannot be undone.`);
    if (!confirmed) return;
    setDeletingId(user.id);
    setBusy(true);
    setMessage('');
    setError('');
    try {
      await deleteAdminUser(user.id);
      await loadUsers();
      setMessage(`@${user.username} was permanently deleted.`);
    } catch (nextError) {
      setError(nextError.message || 'Could not delete account.');
    } finally {
      setDeletingId(null);
      setBusy(false);
    }
  };

  return <main className="view admin-metrics-view admin-accounts-view">
    <header className="admin-metrics-header"><button onClick={onBack}><ArrowLeft /> Profile</button><div><span>{adminName} · private admin</span><h1>Accounts</h1></div><UsersRound /></header>
    <div className="admin-workspace-tabs" role="tablist" aria-label="Admin workspace"><button role="tab" aria-selected="false" onClick={onOpenActivity}>Activity</button><button className="active" role="tab" aria-selected="true">Accounts</button><button role="tab" aria-selected="false" onClick={onOpenDevAttention}>Dev attention</button></div>
    <section className="account-create-panel" aria-labelledby="account-create-title">
      <div className="metrics-section-heading"><div><span>New tester</span><h2 id="account-create-title">Create account</h2></div><Plus /></div>
      <form onSubmit={createAccount}>
        <label><span>Username</span><input value={form.username} onChange={event => setForm(current => ({ ...current, username: event.target.value }))} autoComplete="off" required /></label>
        <label><span>Display name</span><input value={form.displayName} onChange={event => setForm(current => ({ ...current, displayName: event.target.value }))} autoComplete="off" placeholder="Optional" /></label>
        <label><span>Starter password</span><input type="password" value={form.password} onChange={event => setForm(current => ({ ...current, password: event.target.value }))} autoComplete="new-password" minLength="8" required /></label>
        <label><span>Starting tier</span><select value={form.tier} onChange={event => setForm(current => ({ ...current, tier: event.target.value }))}>{ADMIN_TIER_OPTIONS.map(option => <option value={option.value} key={option.value}>{option.label}</option>)}</select></label>
        <button className="save-capture" disabled={busy}>{busy ? 'Saving…' : 'Create account'} <ArrowRight /></button>
      </form>
      {message ? <p className="admin-form-message">{message}</p> : null}
      {error ? <p className="metrics-error" role="alert">{error}</p> : null}
    </section>
    <section className="account-list-panel" aria-labelledby="account-list-title">
      <div className="metrics-section-heading"><div><span>Private roster</span><h2 id="account-list-title">All accounts</h2></div><UsersRound /></div>
      <div className="account-list">{users.map(user => <article key={user.id}>
        <div className="account-list-identity"><span className="profile-medallion">{profileInitial(user)}</span><div><strong>{user.display_name || user.username}</strong><small>@{user.username} · {user.role === 'admin' ? 'Admin' : 'User'} · {formatAccountBytes(user.used_bytes)} used</small>{user.referral_code ? <small className="account-referral-code">Referral: {user.referral_code}</small> : null}</div></div>
        <div className="account-list-controls"><select aria-label={`Tier for ${user.username}`} defaultValue={user.tier} onChange={event => changeTier(user.id, event.target.value)} disabled={busy}>{ADMIN_ACCOUNT_TIER_OPTIONS.map(option => <option value={option.value} key={option.value}>{option.label}</option>)}</select>{user.role === 'user' ? <button type="button" className="admin-delete-account" aria-label={`Delete ${user.username}`} title="Permanently delete account" onClick={() => deleteAccount(user)} disabled={busy}><Trash2 />{deletingId === user.id ? 'Deleting…' : 'Delete'}</button> : <small className="admin-protected-account">Protected</small>}</div>
      </article>)}</div>
    </section>
  </main>;
}

function AdminDevAttentionView({ onBack, onOpenActivity, onOpenAccounts, adminName }) {
  const [entries, setEntries] = useState([]);
  const [resetRequests, setResetRequests] = useState([]);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    Promise.all([getAdminDevAttention(), getAdminPasswordResetRequests()])
      .then(([attention, resets]) => {
        if (!active) return;
        setEntries(attention.entries || []);
        setResetRequests(resets.requests || []);
      })
      .catch(nextError => active && setError(nextError.message || 'Development attention is unavailable.'));
    return () => { active = false; };
  }, []);
  return <main className="view admin-metrics-view dev-attention-view">
    <header className="admin-metrics-header"><button onClick={onBack}><ArrowLeft /> Profile</button><div><span>{adminName} · private admin</span><h1>Dev attention</h1></div><Bug /></header>
    <div className="admin-workspace-tabs" role="tablist" aria-label="Admin workspace"><button role="tab" aria-selected="false" onClick={onOpenActivity}>Activity</button><button role="tab" aria-selected="false" onClick={onOpenAccounts}>Accounts</button><button className="active" role="tab" aria-selected="true">Dev attention</button></div>
    <section className="metrics-section dev-attention-list" aria-labelledby="dev-attention-title">
      <div className="metrics-section-heading"><div><span>Private crash reports</span><h2 id="dev-attention-title">Needs a look</h2></div><Bug /></div>
      {error ? <p className="metrics-error" role="alert">{error}</p> : entries.length ? <div>{entries.map(entry => <article key={entry.id}><div className="dev-attention-summary"><span className="profile-medallion">{profileInitial(entry)}</span><div><strong>{entry.message}</strong><small>@{entry.username} · {entry.route} · {entry.viewport || 'viewport unknown'} · {entry.build || 'build unknown'}</small></div><time>{formatActivityDate(entry.createdAt)}</time></div><details><summary>Technical details</summary><pre>{[entry.stack, entry.componentStack].filter(Boolean).join('\n\n') || 'No stack trace recorded.'}</pre></details></article>)}</div> : <p>No development attention reports yet.</p>}
    </section>
    <section className="metrics-section reset-request-list" aria-labelledby="reset-request-title">
      <div className="metrics-section-heading"><div><span>Account access</span><h2 id="reset-request-title">Password reset requests</h2></div><KeyRound /></div>
      {resetRequests.length ? <div>{resetRequests.map(request => <article key={request.id}><div className="dev-attention-summary"><span className="profile-medallion">?</span><div><strong>@{request.username}</strong><small>{request.status} · {formatActivityDate(request.created_at || request.createdAt)}</small></div><time>{formatActivityDate(request.created_at || request.createdAt)}</time></div>{request.note ? <p>{request.note}</p> : <p>No additional note supplied.</p>}<small className="reset-request-agent">{request.user_agent || 'Device details not supplied.'}</small></article>)}</div> : <p>No password reset requests.</p>}
    </section>
  </main>;
}

function SyncPartiesPanel({ aiSession, onAiSession, showToast }) {
  const [partyName, setPartyName] = useState('');
  const [partyCode, setPartyCode] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [busy, setBusy] = useState(false);
  const parties = aiSession.parties || [];

  useEffect(() => {
    if (!aiSession.token) return;
    let active = true;
    getMirrorParties().then(session => active && onAiSession(session)).catch(() => {});
    return () => { active = false; };
  }, [aiSession.token, onAiSession]);

  const runPartyAction = async action => {
    setBusy(true);
    try {
      const session = await action();
      onAiSession(session);
      return session;
    } catch (error) {
      if (!error.cooldown) showToast(error.message || 'Sync party action failed.');
      return null;
    } finally {
      setBusy(false);
    }
  };
  const create = async event => {
    event.preventDefault();
    if (!partyName.trim()) return;
    const session = await runPartyAction(() => createMirrorParty(partyName.trim()));
    if (session?.inviteCode) setInviteCode(session.inviteCode);
    if (session) setPartyName('');
  };
  const join = async event => {
    event.preventDefault();
    if (!partyCode.trim()) return;
    const session = await runPartyAction(() => joinMirrorParty(partyCode.trim()));
    if (session) setPartyCode('');
  };
  const leave = async party => {
    if (!window.confirm(`Leave ${party.name}?`)) return;
    await runPartyAction(() => leaveMirrorParty(party.id));
  };
  const copyInvite = async () => {
    try { await navigator.clipboard.writeText(inviteCode); showToast('Sync party invite copied.'); } catch { showToast(`Share this invite code: ${inviteCode}`); }
  };

  return <section className="sync-parties-panel" aria-labelledby="sync-parties-title">
    <div className="ai-account-heading"><UsersRound /><div><span>Shared privately</span><h2 id="sync-parties-title">Sync parties</h2></div></div>
    <p className="sync-parties-lede">Create up to 5 parties. Each party holds up to 12 people for shared calendar plans, fit posts, and comparisons.</p>
    {!aiSession.token ? <p className="connect-hint"><LockKeyhole /> Sign in to create or join a sync party.</p> : <>
      <div className="sync-party-forms"><form onSubmit={create}><label><span>New party</span><input value={partyName} onChange={event => setPartyName(event.target.value)} placeholder="e.g. House wardrobe" maxLength="80" /></label><button disabled={busy || !partyName.trim()}><Plus /> Create</button></form><form onSubmit={join}><label><span>Join with code</span><input value={partyCode} onChange={event => setPartyCode(event.target.value)} placeholder="M••••••••" maxLength="64" /></label><button disabled={busy || !partyCode.trim()}><Link2 /> Join</button></form></div>
      {inviteCode ? <div className="sync-party-invite"><span>New invite code</span><strong>{inviteCode}</strong><button onClick={copyInvite}>Copy code</button></div> : null}
      <div className="sync-party-list" aria-label="Your sync parties">{parties.length ? parties.map(party => <article key={party.id}><div className="sync-party-heading"><div><strong>{party.name}</strong><small>{party.memberCount}/{party.maxMembers} members · {party.memberCount >= party.maxMembers ? 'Full' : 'Open'}</small></div><button onClick={() => leave(party)} aria-label={`Leave ${party.name}`}>Leave</button></div><div className="sync-party-members">{party.members.map(member => <span key={member.id} className={member.isCurrent ? 'current' : ''}>{member.displayName}</span>)}</div></article>) : <p className="sync-party-empty">No parties yet. Make one for the household, a watch group, or friends matching fits.</p>}</div>
    </>}
  </section>;
}

function AppUpdateNotice({ showToast }) {
  const [update, setUpdate] = useState(null);
  useEffect(() => {
    let active = true;
    const check = async () => {
      try {
        const response = await fetch(`${import.meta.env.BASE_URL}sw.js?check=${Date.now()}`, { cache: 'no-store' });
        const source = await response.text();
        const deployed = source.match(/mirror-beta-v([^'";]+)/)?.[1] || '';
        const current = BUILD_VERSION.replace(/^v/, '');
        if (active && deployed && deployed !== current) setUpdate(deployed);
      } catch { /* Update checks are helpful, never blocking. */ }
    };
    void check();
    return () => { active = false; };
  }, []);
  if (!update) return null;
  const hardRefresh = async () => {
    try { await navigator.serviceWorker?.getRegistration(import.meta.env.BASE_URL).then(registration => registration?.update()); } catch { /* Continue with the cache-busting reload. */ }
    const url = new URL(window.location.href);
    url.searchParams.set('refresh', String(Date.now()));
    window.location.replace(url.toString());
  };
  return <section className="app-update-notice" aria-labelledby="app-update-title"><div><span className="app-update-badge">New build ready</span><h2 id="app-update-title">MIЯЯOЯ has a fresh layer.</h2><p>New location cleanup, clearer move destinations, stronger search, and the first friend-invite achievements are ready to load.</p></div><button onClick={hardRefresh}><RefreshCw /> Hard refresh</button></section>;
}

function TabletWorkspaceBanner() {
  return <section className="tablet-workspace-banner" aria-labelledby="tablet-workspace-title"><img src="./assets/tablet-surroundings-banner.png" alt="Tablet beside organized clothing, books, a plant, and storage boxes" /><div className="tablet-workspace-copy"><span>Desktop / tablet app</span><h2 id="tablet-workspace-title">Give your library room to breathe.</h2><p>Use the Desktop / Tablet app to sort closets, name sections, explore Surroundings, and see the patterns your phone captures.</p><a href={`${import.meta.env.BASE_URL}?view=closet`}>Use the Desktop / Tablet app <ArrowRight /></a></div></section>;
}

function AchievementsPanel({ state, parties, referralCode, showToast }) {
  const summary = getAchievementSummary(state, parties);
  const referralReady = summary.referralUnlocked && Boolean(referralCode);
  const copyReferral = async () => {
    const link = referralUrl(referralCode);
    try { await navigator.clipboard.writeText(link); showToast?.('Friend invite link copied.'); } catch { showToast?.(link); }
  };
  return <section className="achievements-panel" aria-labelledby="achievements-title"><div className="achievements-heading"><div><span>Quiet progress</span><h2 id="achievements-title">Your MIRROR milestones</h2></div><strong>{summary.unlockedCount}/{summary.total}</strong></div><p className="achievements-lede">A light reflection of how you use the app. Nothing here is public, and no detailed behavior history is kept.</p><div className="achievement-list">{summary.achievements.map(achievement => <div className={`achievement-row ${achievement.unlocked ? 'unlocked' : ''}`} key={achievement.id}><span>{achievement.unlocked ? <Check /> : <LockKeyhole />}</span><div><strong>{achievement.title}</strong><small>{achievement.detail}</small></div></div>)}</div>{referralReady ? <div className="referral-unlocked"><div><span>Unlocked</span><strong>Invite friends into a private sync party.</strong><small>Share a starting link, then create or join a party below to compare fits and plans.</small></div><button onClick={copyReferral}><UsersRound /> Copy invite link</button></div> : summary.referralUnlocked ? <p className="achievement-locked-note">Sign in to receive a private invite code. The link will count which accounts you bring in.</p> : <p className="achievement-locked-note">Unlock three milestones to open the friend invite path.</p>}</section>;
}

function AuthSplash({ onSignedIn }) {
  const [mode, setMode] = useState('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [familyCode, setFamilyCode] = useState('');
  const [accountForm, setAccountForm] = useState({ username: '', displayName: '', password: '', accessCode: '' });
  const [resetUsername, setResetUsername] = useState('');
  const [resetNote, setResetNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    const referral = new URLSearchParams(window.location.search).get('ref') || '';
    let stored = '';
    try { stored = localStorage.getItem('mirror_referral_code') || ''; } catch { /* Continue without referral storage. */ }
    const accessCode = referral || stored;
    if (accessCode) {
      setAccountForm(current => ({ ...current, accessCode: accessCode.toUpperCase() }));
      if (referral) setMode('account');
    }
  }, []);

  const showError = error => setNotice(error?.message || 'MIЯЯOЯ could not open this account.');

  const signIn = async event => {
    event.preventDefault();
    setBusy(true);
    setNotice('');
    try {
      let referralCode = '';
      try { referralCode = localStorage.getItem('mirror_referral_code') || ''; } catch { /* Continue without attribution. */ }
      const session = await loginToMirror(username, password, familyCode, referralCode);
      try { localStorage.removeItem('mirror_referral_code'); } catch { /* Ignore restricted storage. */ }
      await onSignedIn(session);
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  const createAccount = async event => {
    event.preventDefault();
    setBusy(true);
    setNotice('');
    try {
      const session = await createMirrorAccount(accountForm);
      try { localStorage.removeItem('mirror_referral_code'); } catch { /* Ignore restricted storage. */ }
      await onSignedIn(session);
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  const requestReset = async event => {
    event.preventDefault();
    setBusy(true);
    setNotice('');
    try {
      const result = await requestPasswordReset(resetUsername, resetNote);
      setNotice(result.message || 'Your password help request is in the private admin queue.');
      setResetNote('');
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return <main className="auth-splash" aria-labelledby="auth-splash-title">
    <div className="auth-splash-sheen" aria-hidden="true" />
    <section className="auth-splash-content">
      <MirrorSplashLogo />
      <span className="auth-splash-kicker">Empirical Skye · private by design</span>
      <h1 id="auth-splash-title">See it clearly.<br /><em>Choose what comes next.</em></h1>
      <p className="auth-splash-lede">A private visual home for what you wear, grow, read, and keep.</p>
      <div className="auth-splash-lenses" aria-label="What Mirror helps you track"><span>Wardrobe</span><span>Plants</span><span>Reads</span><span>Home &amp; objects</span></div>

      {mode === 'login' ? <form className="auth-splash-form" onSubmit={signIn}>
        <label><span>Username</span><input autoFocus autoComplete="username" value={username} onChange={event => setUsername(event.target.value)} required /></label>
        <label><span>Password</span><input type="password" autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} required /></label>
        <label><span>Family code <small>optional</small></span><input value={familyCode} onChange={event => setFamilyCode(event.target.value)} placeholder="Use the same code to link profiles" /></label>
        <button className="auth-splash-primary" disabled={busy} type="submit"><LogIn /> {busy ? 'Opening…' : <>Open my <MirrorWord /></>}</button>
        <button className="auth-demo-window" type="button" onClick={() => { setUsername('MIRRORDEMO'); setNotice('MOCK username placed. Enter the private demo password to open the view-only window.'); }}><Eye /> Open the protected MOCK window</button>
      </form> : null}

      {mode === 'account' ? <form className="auth-splash-form" onSubmit={createAccount}>
        <div className="auth-splash-form-heading"><span>Invite-only account access</span><strong>No open sign-up.</strong><small>Use an admin invite or a referral code issued by an existing account.</small></div>
        <label><span>Username</span><input autoFocus autoComplete="username" value={accountForm.username} onChange={event => setAccountForm(current => ({ ...current, username: event.target.value }))} required /></label>
        <label><span>Display name <small>optional</small></span><input autoComplete="name" value={accountForm.displayName} onChange={event => setAccountForm(current => ({ ...current, displayName: event.target.value }))} /></label>
        <label><span>Password</span><input type="password" autoComplete="new-password" minLength="8" value={accountForm.password} onChange={event => setAccountForm(current => ({ ...current, password: event.target.value }))} required /></label>
        <label><span>Referral / admin code</span><input autoComplete="off" value={accountForm.accessCode} onChange={event => setAccountForm(current => ({ ...current, accessCode: event.target.value }))} required /></label>
        <button className="auth-splash-primary" disabled={busy} type="submit">{busy ? 'Creating…' : 'Create private account'} <ArrowRight /></button>
      </form> : null}

      {mode === 'reset' ? <form className="auth-splash-form" onSubmit={requestReset}>
        <div className="auth-splash-form-heading"><span>Private admin queue</span><strong>Request password help.</strong><small>No email is required. Your request will be reviewed from Admin → Dev attention.</small></div>
        <label><span>Username</span><input autoFocus autoComplete="username" value={resetUsername} onChange={event => setResetUsername(event.target.value)} required /></label>
        <label><span>What should we know? <small>optional</small></span><textarea value={resetNote} onChange={event => setResetNote(event.target.value)} maxLength="600" placeholder="A safe way to reach you or what changed" /></label>
        <button className="auth-splash-primary" disabled={busy} type="submit">{busy ? 'Sending…' : 'Send password help'} <ArrowRight /></button>
      </form> : null}

      <div className="auth-splash-actions">
        <button type="button" className={mode === 'login' ? 'active' : ''} onClick={() => { setMode('login'); setNotice(''); }}>Sign in</button>
        <button type="button" className={mode === 'account' ? 'active' : ''} onClick={() => { setMode('account'); setNotice(''); }}>Referral / invite</button>
        <button type="button" className={mode === 'reset' ? 'active' : ''} onClick={() => { setMode('reset'); setNotice(''); }}>Password help</button>
      </div>
      {notice ? <p className="auth-splash-notice" role="status">{notice}</p> : null}
      <small className="auth-splash-footer">Build {BUILD_VERSION} · a private reflection of what you wear, grow, read, and care for.</small>
    </section>
  </main>;
}

function ProfileView({
  state,
  refresh,
  onImport,
  importRef,
  aiSession,
  onAiSession,
  onSignedIn,
  onProfileSelected,
  onSignedOut,
  showToast,
  sync,
  onAbortSync,
  onResumeSync,
  onOpenAdmin,
  onOpenGuides,
  onOpenImportHub,
  isGuideAuthor,
}) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginFamilyCode, setLoginFamilyCode] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [familyCode, setFamilyCode] = useState('');
  const [familyBusy, setFamilyBusy] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [profilePassword, setProfilePassword] = useState('');
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountForm, setAccountForm] = useState({ username: '', displayName: '', password: '', accessCode: '' });
  const [resetOpen, setResetOpen] = useState(false);
  const [resetUsername, setResetUsername] = useState('');
  const [resetNote, setResetNote] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const itemCount = flattenState(state).length;
  const quota = aiSession.quota || {};
  const aiQuota = quota.ai || {};
  const isPremium = ['premium', 'owner'].includes(aiSession.user?.tier);
  const familyMembers = aiSession.family?.members || [];
  const selectedProfile = familyMembers.find(member => member.id === selectedProfileId) || null;
  const threadUsage = getThreadUsage(aiQuota);
  const aiPromotion = aiQuota.promotion;
  const storageUsage = getStorageUsage(quota, aiSession.user?.quotaBytes);
  const monthlyReset = nextMonthlyResetLabel(aiQuota);

  useEffect(() => {
    const handler = event => {
      event.preventDefault();
      setDeferredPrompt(event);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    try {
      const code = localStorage.getItem('mirror_referral_code') || '';
      if (code) {
        setAccountForm(current => ({ ...current, accessCode: code }));
        setAccountOpen(true);
      }
    } catch { /* Referral access is optional when storage is restricted. */ }
  }, []);

  useEffect(() => {
    if (!aiSession.token) return;
    let active = true;
    refreshMirrorSession()
      .then(session => {
        if (active) onAiSession(session);
        return getMirrorFamilyProfiles();
      })
      .then(session => active && onAiSession(session))
      .catch(() => {});
    return () => { active = false; };
  }, [aiSession.token, onAiSession]);

  const toggleDefaultToSurroundings = event => {
    Store.updatePreferences({ defaultToSurroundings: event.target.checked });
    refresh();
  };

  const chooseDefaultSurroundingBranch = event => {
    Store.updatePreferences({ defaultSurroundingBranch: event.target.value });
    refresh();
  };

  const chooseSurroundingsInterface = event => {
    Store.updatePreferences({ surroundingsInterface: event.target.value });
    refresh();
  };

  const toggleSurroundingAiAssist = event => {
    Store.updatePreferences({ surroundingAiAssist: event.target.checked });
    refresh();
  };

  const toggleBrowserNotifications = async event => {
    if (!event.target.checked) {
      Store.updatePreferences({ browserNotifications: false });
      refresh();
      return;
    }
    const permission = await requestBrowserNotificationPermission();
    if (permission !== 'granted') {
      showToast(permission === 'denied' ? 'Browser notifications are blocked for this site. Re-enable them in browser settings.' : 'This browser does not support notifications here.');
      Store.updatePreferences({ browserNotifications: false });
      refresh();
      return;
    }
    Store.updatePreferences({ browserNotifications: true });
    refresh();
    void showMirrorNotification('MIЯЯOЯ notifications on', { body: 'Your tracked wardrobe and Surrounding schedule can now reflect back to you.', tag: 'mirror-notification-test', data: { url: `${import.meta.env.BASE_URL}?view=schedule` } }).catch(() => {});
    showToast('Browser notifications enabled.');
  };

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    } else {
      window.alert('Use your browser menu and choose “Add to Home Screen” to install MIЯЯOЯ.');
    }
  };

  const signIn = async event => {
    event.preventDefault();
    setAuthBusy(true);
    try {
      let referralCode = '';
      try { referralCode = localStorage.getItem('mirror_referral_code') || ''; } catch { /* Continue without attribution when storage is restricted. */ }
      const session = await loginToMirror(username, password, loginFamilyCode, referralCode);
      try { localStorage.removeItem('mirror_referral_code'); } catch { /* Ignore restricted storage. */ }
      setPassword('');
      setLoginFamilyCode('');
      await onSignedIn(session);
    } catch (error) {
      if (!error.cooldown) showToast(error.message || 'Sign-in failed.');
    } finally {
      setAuthBusy(false);
    }
  };

  const createAccount = async event => {
    event.preventDefault();
    setAuthBusy(true);
    try {
      const session = await createMirrorAccount(accountForm);
      setAccountForm({ username: '', displayName: '', password: '', accessCode: '' });
      try { localStorage.removeItem('mirror_referral_code'); } catch { /* Ignore restricted storage. */ }
      setAccountOpen(false);
      await onSignedIn(session);
      showToast('Account created. Your private wardrobe is ready.');
    } catch (error) {
      if (!error.cooldown) showToast(error.message || 'Could not create the account.');
    } finally {
      setAuthBusy(false);
    }
  };

  const submitPasswordReset = async event => {
    event.preventDefault();
    setResetBusy(true);
    try {
      const result = await requestPasswordReset(resetUsername, resetNote);
      setResetUsername('');
      setResetNote('');
      setResetOpen(false);
      showToast(result.message || 'Reset request sent to the private admin queue.');
    } catch (error) {
      if (!error.cooldown) showToast(error.message || 'Could not send the reset request.');
    } finally {
      setResetBusy(false);
    }
  };

  const linkFamily = async event => {
    event.preventDefault();
    if (!familyCode.trim()) return;
    setFamilyBusy(true);
    try {
      const session = await linkMirrorFamily(familyCode);
      onAiSession(session);
      setFamilyCode('');
      showToast('Linked profiles are ready. Each profile keeps its own private workspace.');
    } catch (error) {
      if (!error.cooldown) showToast(error.message || 'Family linking failed.');
    } finally {
      setFamilyBusy(false);
    }
  };

  const switchProfile = async event => {
    event.preventDefault();
    if (!selectedProfile || selectedProfile.isCurrent) return;
    setFamilyBusy(true);
    try {
      const session = await selectMirrorFamilyProfile(selectedProfile.id, profilePassword);
      setProfilePassword('');
      await onProfileSelected(session);
    } catch (error) {
      if (!error.cooldown) showToast(error.message || 'Could not open that profile.');
    } finally {
      setFamilyBusy(false);
    }
  };

  const updatePassword = async event => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      showToast('The new passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      showToast('Use at least 8 characters for the new password.');
      return;
    }
    setPasswordBusy(true);
    try {
      const session = await changeMirrorPassword(currentPassword, newPassword);
      onAiSession(session);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordOpen(false);
      announceProfileChange();
      showToast('Password updated. Other signed-in devices must sign in again.');
    } catch (error) {
      if (!error.cooldown) showToast(error.message || 'Could not update the password.');
    } finally {
      setPasswordBusy(false);
    }
  };

  return (
    <main className="view simple-view profile-view">
      <span className="page-kicker">Empirical Skye</span>
      <h1>Your <MirrorWord />.</h1>
      <p className="page-lede">Private by default. Portable by design. Your closet remains yours.</p>
      <SyncPill sync={sync} onAbort={onAbortSync} onResume={onResumeSync} />
      <AppUpdateNotice showToast={showToast} />
      <div className="profile-stats"><div><strong>{itemCount}</strong><span>pieces</span></div><div><strong>{state.mirror?.outfits?.length || 0}</strong><span>looks</span></div><div><strong>{state.collections?.length || 0}</strong><span>spaces</span></div></div>

      <TabletWorkspaceBanner />
      <details className="profile-collapsible"><summary><span><small>Quiet progress</small><strong>Milestones &amp; friend invites</strong></span><ChevronDown /></summary><AchievementsPanel state={state} parties={aiSession.parties || []} referralCode={aiSession.user?.referralCode} showToast={showToast} /></details>

      <details className="profile-collapsible"><summary><span><small>Preferences</small><strong>Surroundings setup</strong></span><ChevronDown /></summary><section className="ai-account-panel">
        <div className="ai-account-heading"><Boxes /><div><span>Preferences</span><h2>Surroundings</h2></div></div>
        <label className="desktop-toggle"><input type="checkbox" checked={!!state.preferences?.defaultToSurroundings} onChange={toggleDefaultToSurroundings} /><span /><strong>Open on Surroundings by default</strong></label>
        <label className="desktop-toggle"><input type="checkbox" checked={!!state.preferences?.surroundingAiAssist} onChange={toggleSurroundingAiAssist} /><span /><strong>Offer AI assist when adding Surrounding records</strong></label>
        <label className="profile-focus-select"><span>Surroundings interface</span><select value={state.preferences?.surroundingsInterface || 'simple'} onChange={chooseSurroundingsInterface}><option value="simple">Simple · lead with what I’m doing</option><option value="advanced">Advanced · rooms and every system</option></select><small>Simple keeps Reads and active care in front; Advanced opens the complete room-and-category map.</small></label>
        <label className="profile-focus-select"><span>Default Surrounding focus</span><select value={state.preferences?.defaultSurroundingBranch || 'all'} onChange={chooseDefaultSurroundingBranch}><option value="all">Choose when I arrive</option>{SURROUNDING_BRANCHES.map(branch => <option value={branch.id} key={branch.id}>{branch.label}</option>)}</select><small>Rooms can override this with their own focus theme.</small></label>
      </section></details>

      <details className="profile-collapsible"><summary><span><small>Start here</small><strong>The Mirror field guide</strong></span><ChevronDown /></summary><section className="ai-account-panel guide-profile-panel">
        <div className="ai-account-heading"><BookOpen /><div><span>Open concept</span><h2>Learn the whole space</h2></div></div>
        <p>A calm walk through closets, rooms, plants, Reads, dates, AI consent, and the small habits that make Mirror useful without trying to catalogue your life all at once.</p>
        <button type="button" className="guide-profile-open" onClick={() => onOpenGuides?.(false)}><BookOpen /> Open the field guide <ArrowRight /></button>
        {isGuideAuthor ? <button type="button" className="guide-profile-open author" onClick={() => onOpenGuides?.(true)}><LockKeyhole /> Open AjP guide studio <ArrowRight /></button> : null}
      </section></details>

      <details className="profile-collapsible"><summary><span><small>Reflect back to you</small><strong>Notifications</strong></span><ChevronDown /></summary><section className="ai-account-panel notification-preferences" aria-labelledby="notification-preferences-title">
        <div className="ai-account-heading"><CalendarDays /><div><span>Reflect back to you</span><h2 id="notification-preferences-title">Notifications</h2></div></div>
        <p className="notification-lede">Your calendar already tracks laundry, socks, worn pieces, plants, books, storage, pantry dates, and manual plans.</p>
        <label className="desktop-toggle"><input type="checkbox" checked={!!state.preferences?.browserNotifications} onChange={toggleBrowserNotifications} disabled={!browserNotificationsSupported() || browserNotificationPermission() === 'denied'} /><span /><strong>{browserNotificationsSupported() ? (browserNotificationPermission() === 'denied' ? 'Browser notifications blocked' : 'Browser / PWA notifications') : 'Browser notifications unavailable'}</strong></label>
        <small className="notification-status">Permission is requested only when you opt in. When the app is opened, due schedule signals can appear as a notification.</small>
      </section></details>

      <details className="profile-collapsible profile-account-collapsible" open={!aiSession.token}><summary><span><small>Account &amp; sync</small><strong>{aiSession.token ? 'Connected profile' : 'Sign in to MIRROR'}</strong></span><ChevronDown /></summary><section className="ai-account-panel">
        <div className="ai-account-heading"><Bot /><div><span><MirrorWord /> intelligence</span><h2>{aiSession.token ? 'Account connected' : 'Connect your account'}</h2></div></div>
        {aiSession.token ? <>
          <div className="signed-in-row"><span className="profile-medallion">{profileInitial(aiSession.user)}</span><div><strong>{aiSession.user?.displayName || aiSession.user?.username || <><MirrorWord /> member</>}</strong><small>@{aiSession.user?.username} · {aiSession.user?.tierLabel || 'Access'}</small></div><CheckCircle2 /></div>

          <div className="usage-gauge-grid">
            <AccountGauge
              icon={Sparkles}
              label={<><MirrorWord /> Threads</>}
              valueLabel={threadUsage.valueLabel}
              detail={threadUsage.mode === 'open' ? 'No monthly Thread cap.' : threadUsage.mode === 'unavailable' ? 'Checking this month’s activity.' : `Refreshes automatically ${monthlyReset} (UTC).`}
              note={threadUsage.mode === 'open'
                ? 'Short safety cooldowns can still happen without using monthly access.'
                : threadUsage.mode === 'finite' && threadUsage.threadsLeft === 0
                  ? 'Private uploads, organizing and sync still work.'
                  : 'Threads are a simple allowance view; scans and Try-On use different amounts.'}
              mode={threadUsage.mode}
              fillPercent={threadUsage.fillPercent}
              meterMax={aiQuota.trial ? aiQuota.dailyTryOnLimit || 1 : MONTHLY_THREAD_SCALE * Math.max(1, Number(aiPromotion?.multiplier) || 1)}
              meterValue={threadUsage.threadsLeft || 0}
              meterText={`${threadUsage.valueLabel}; refreshes ${monthlyReset} UTC.`}
            />
            <AccountGauge
              icon={HardDrive}
              label="Media Room"
              valueLabel={storageUsage.valueLabel}
              detail={storageUsage.mode === 'open' ? 'Ongoing · no account storage cap.' : storageUsage.mode === 'unavailable' ? 'Checking private media capacity.' : 'Ongoing · does not reset monthly.'}
              note={storageUsage.mode === 'open' ? 'Your private media room stays open.' : 'Removing private media opens room again.'}
              mode={storageUsage.mode}
              fillPercent={storageUsage.fillPercent}
              meterValue={storageUsage.availablePercent || 0}
              meterText={`${storageUsage.availablePercent || 0} percent of private storage is available; storage does not reset monthly.`}
            />
          </div>
          <p className="usage-clarifier">Monthly Threads track account usage. A countdown is temporary service pacing—not your monthly refresh.</p>
          <p className="tier-clarity">{isPremium
            ? 'Premium: new captures can scan automatically. Your OpenAI key stays protected on the VPS.'
            : aiQuota.trial
              ? 'Trial: 50 MB private storage and one Try-On render per UTC day.'
            : aiPromotion
              ? <>Verified User: the 3× AI test promotion is active this month. Your allowance resets {monthlyReset} (UTC).</>
              : <>Verified User: uploads and sync stay available. Start scans when you choose; they use monthly <MirrorWord /> Threads.</>}</p>
          {aiSession.user?.role === 'admin' ? <button className="admin-metrics-link" onClick={onOpenAdmin}><BarChart3 /><span><strong>Open activity metrics</strong><small>Usage, hot hours, and recent users</small></span><ArrowRight /></button> : null}

          <SyncPartiesPanel aiSession={aiSession} onAiSession={onAiSession} showToast={showToast} />

          <section className="family-panel" aria-labelledby="family-title">
            <div className="subpanel-heading"><UsersRound /><div><span>Linked profiles</span><h3 id="family-title">Choose your <MirrorWord /></h3></div></div>
            {familyMembers.length ? <>
              <p>Profile choosing with a safety lock: every profile has a separate wardrobe on this device and still requires its own password.</p>
              <div className="profile-chooser">
                {familyMembers.map(member => <button className={`${member.isCurrent ? 'current' : ''} ${selectedProfileId === member.id ? 'selected' : ''}`} key={member.id} onClick={() => setSelectedProfileId(member.id)} disabled={member.isCurrent}><span>{profileInitial(member)}</span><strong>{member.displayName}</strong><small>{member.isCurrent ? 'Current' : member.tierLabel}</small></button>)}
              </div>
              {selectedProfile && !selectedProfile.isCurrent ? <form className="profile-unlock" onSubmit={switchProfile}>
                <label><span>{selectedProfile.displayName}'s password</span><input type="password" autoComplete="current-password" value={profilePassword} onChange={event => setProfilePassword(event.target.value)} required /></label>
                <button disabled={familyBusy}><LockKeyhole /> {familyBusy ? 'Opening…' : `Open ${selectedProfile.displayName}`}</button>
              </form> : null}
            </> : <form className="family-link-form" onSubmit={linkFamily}>
              <p>Enter the same private code on each account to make a profile group. The code links accounts; it never replaces a password.</p>
              <label><span>Private family code</span><input value={familyCode} onChange={event => setFamilyCode(event.target.value)} minLength="8" maxLength="64" placeholder="At least 8 letters + numbers" required /></label>
              <button disabled={familyBusy}><Link2 /> {familyBusy ? 'Linking…' : 'Create or join group'}</button>
            </form>}
          </section>

          <section className="password-panel">
            <button className="subpanel-toggle" onClick={() => setPasswordOpen(value => !value)} aria-expanded={passwordOpen}><KeyRound /><span><strong>Change password</strong><small>Replace the starter password for this profile</small></span><ChevronDown /></button>
            {passwordOpen ? <form className="password-form" onSubmit={updatePassword}>
              <label><span>Current password</span><input type="password" autoComplete="current-password" value={currentPassword} onChange={event => setCurrentPassword(event.target.value)} required /></label>
              <label><span>New password</span><input type="password" autoComplete="new-password" value={newPassword} onChange={event => setNewPassword(event.target.value)} minLength="8" required /></label>
              <label><span>Confirm new password</span><input type="password" autoComplete="new-password" value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} minLength="8" required /></label>
              <button disabled={passwordBusy}>{passwordBusy ? 'Updating…' : 'Update password'}</button>
            </form> : null}
          </section>

          <button className="account-action" onClick={onSignedOut}><LogOut /> Sign out of this profile</button>
        </> : <div className="login-access-wrap"><form className="login-form" onSubmit={signIn}>
          <p>Sign in to open a private wardrobe, VPS media storage, and the AI access included with that account. Your provider key never enters this browser.</p>
          <label><span>Username</span><input autoComplete="username" value={username} onChange={event => setUsername(event.target.value)} required /></label>
          <label><span>Password</span><input type="password" autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} required /></label>
          <label><span>Family code <small>optional</small></span><input value={loginFamilyCode} onChange={event => setLoginFamilyCode(event.target.value)} placeholder="Use the same code to link profiles" /></label>
          <button disabled={authBusy} type="submit"><LogIn /> {authBusy ? 'Opening…' : <>Open my <MirrorWord /></>}</button>
          <div className="tier-preview"><div><strong>Premium</strong><span>Open media room · automatic + manual AI</span></div><div><strong>Verified User</strong><span>Private media room · monthly <MirrorWord /> Threads · manual AI</span></div></div>
        </form>
        <div className="auth-secondary-actions"><button type="button" onClick={() => setAccountOpen(value => !value)}><Plus /> Have a referral or admin invite?</button><button type="button" onClick={() => setResetOpen(value => !value)}><KeyRound /> Request password help</button></div>
        {accountOpen ? <form className="invite-account-form" onSubmit={createAccount}><div><span>Invite-only account access</span><strong>No open sign-up.</strong><small>Use an admin invite or a referral code issued by an existing account.</small></div><label><span>Username</span><input value={accountForm.username} onChange={event => setAccountForm(current => ({ ...current, username: event.target.value }))} autoComplete="username" required /></label><label><span>Display name</span><input value={accountForm.displayName} onChange={event => setAccountForm(current => ({ ...current, displayName: event.target.value }))} autoComplete="name" placeholder="Optional" /></label><label><span>Password</span><input type="password" value={accountForm.password} onChange={event => setAccountForm(current => ({ ...current, password: event.target.value }))} autoComplete="new-password" minLength="8" required /></label><label><span>Referral / admin code</span><input value={accountForm.accessCode} onChange={event => setAccountForm(current => ({ ...current, accessCode: event.target.value }))} autoComplete="off" required /></label><button disabled={authBusy} type="submit">{authBusy ? 'Creating…' : 'Create private account'} <ArrowRight /></button></form> : null}
        {resetOpen ? <form className="password-help-form" onSubmit={submitPasswordReset}><div><span>Private admin queue</span><strong>Request password help.</strong><small>No email is required yet. We will review the request from Admin → Dev attention.</small></div><label><span>Username</span><input value={resetUsername} onChange={event => setResetUsername(event.target.value)} autoComplete="username" required /></label><label><span>What should we know? <small>optional</small></span><textarea value={resetNote} onChange={event => setResetNote(event.target.value)} maxLength="600" placeholder="A safe way to reach you or what changed" /></label><button disabled={resetBusy} type="submit">{resetBusy ? 'Sending…' : 'Send reset request'} <ArrowRight /></button></form> : null}
        </div>}
      </section></details>

      <details className="profile-collapsible profile-advanced-collapsible"><summary><span><small>Advanced</small><strong>Install, import, and export</strong></span><ChevronDown /></summary><div className="settings-list">
        <button onClick={install}><Download /><span><strong>Install <MirrorWord /></strong><small>Add the PWA to your home screen</small></span><ChevronRight /></button>
        <button onClick={onOpenImportHub}><FolderOpen /><span><strong>Bring in a calendar or closet export</strong><small>Review ICS, CSV, or closet JSON before adding it</small></span><ChevronRight /></button>
        <button onClick={() => importRef.current?.click()}><Upload /><span><strong>Replace with a MIЯЯOЯ backup</strong><small>Import a classic MIЯЯOЯ ZIP or legacy JSON snapshot</small></span><ChevronRight /></button>
        <button onClick={() => downloadState(state)}><Archive /><span><strong>Export portable data</strong><small>Download a portable JSON snapshot</small></span><ChevronRight /></button>
      </div><AiZerepCredit /></details>
      <input ref={importRef} type="file" accept=".zip,.json,application/zip,application/json" hidden onChange={onImport} />
      <p className="privacy-note">Signed-in wardrobes now sync across devices with revision checks. Photos use opaque private media IDs, authenticated owner checks, and no-cache delivery; linked profiles cannot open another profile's photos without selecting it with its password.</p>
    </main>
  );
}

const BULK_PHOTO_ROLES = [
  { key: 'front', label: 'Front' },
  { key: 'back', label: 'Back' },
  { key: 'tag', label: 'Tag' },
];

function CaptureSheet({ open, onClose, onSaved, aiSession }) {
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);
  const backRef = useRef(null);
  const tagRef = useRef(null);
  const bulkRef = useRef(null);
  const [imageData, setImageData] = useState('');
  const [backImageData, setBackImageData] = useState('');
  const [tagImageData, setTagImageData] = useState('');
  const [captureMode, setCaptureMode] = useState('single');
  const [bulkGroups, setBulkGroups] = useState([]);
  const [bulkSwapSelection, setBulkSwapSelection] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [workwear, setWorkwear] = useState(false);
  const [profileType, setProfileType] = useState('clothing');
  const [label, setLabel] = useState('');
  const [intent, setIntent] = useState({ wear: false, sell: false });
  const [scanAfterSave, setScanAfterSave] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const autoAllowed = !!(aiSession.user?.aiAutoScanAllowed ?? aiSession.quota?.ai?.autoScanAllowed);
  const captureThreads = compactThreadLabel(aiSession.quota?.ai);
  useEffect(() => {
    if (open) setScanAfterSave(autoAllowed);
  }, [autoAllowed, open]);
  const choose = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setPhotoError('');
    try {
      setImageData(await fileToOptimizedImage(file));
    } catch (error) {
      setPhotoError(error.message || 'That photo could not be prepared.');
    }
  };
  const chooseTag = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setPhotoError('');
    try {
      setTagImageData(await fileToOptimizedImage(file, { maxDimension: 1200, minDimension: 720, targetBytes: 500 * 1024 }));
    } catch (error) {
      setPhotoError(error.message || 'That care-label photo could not be prepared.');
    }
  };
  const chooseBack = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setPhotoError('');
    try {
      setBackImageData(await fileToOptimizedImage(file));
    } catch (error) {
      setPhotoError(error.message || 'That back photo could not be prepared.');
    }
  };
  const chooseBulk = async event => {
    const files = [...(event.target.files || [])];
    event.target.value = '';
    if (!files.length) return;
    if (files.length > 30) {
      setPhotoError('Choose up to 30 photos at once: ten FRONT / BACK / TAG sets.');
      return;
    }
    setPhotoError('');
    setBulkBusy(true);
    try {
      const prepared = [];
      for (let start = 0; start < files.length; start += 3) {
        const batch = files.slice(start, start + 3);
        const images = await Promise.all(batch.map((file, index) => fileToOptimizedImage(file, index === 2
          ? { maxDimension: 1200, minDimension: 720, targetBytes: 500 * 1024 }
          : undefined)));
        prepared.push(...images);
      }
      const nextGroups = [];
      for (let index = 0; index < prepared.length; index += 3) {
        const frontFile = files[index];
        nextGroups.push({
          id: `bulk-${Date.now()}-${index}`,
          label: String(frontFile?.name || '').replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim(),
          front: prepared[index] || '',
          back: prepared[index + 1] || '',
          tag: prepared[index + 2] || '',
        });
      }
      setBulkGroups(nextGroups);
      setBulkSwapSelection(null);
    } catch (error) {
      setPhotoError(error.message || 'That bulk photo set could not be prepared.');
    } finally {
      setBulkBusy(false);
    }
  };
  const updateBulkLabel = (id, nextLabel) => setBulkGroups(current => current.map(group => group.id === id ? { ...group, label: nextLabel } : group));
  const removeBulkGroup = id => {
    setBulkGroups(current => current.filter(group => group.id !== id));
    setBulkSwapSelection(current => current?.groupId === id ? null : current);
  };
  const selectBulkPhotoSlot = (groupId, role) => {
    if (!bulkSwapSelection) {
      setBulkSwapSelection({ groupId, role });
      return;
    }
    if (bulkSwapSelection.groupId === groupId && bulkSwapSelection.role === role) {
      setBulkSwapSelection(null);
      return;
    }
    setBulkGroups(current => {
      const firstGroup = current.find(group => group.id === bulkSwapSelection.groupId);
      const secondGroup = current.find(group => group.id === groupId);
      if (!firstGroup || !secondGroup) return current;
      const firstPhoto = firstGroup[bulkSwapSelection.role] || '';
      const secondPhoto = secondGroup[role] || '';
      return current.map(group => {
        let next = group;
        if (group.id === bulkSwapSelection.groupId) next = { ...next, [bulkSwapSelection.role]: secondPhoto };
        if (group.id === groupId) next = { ...next, [role]: firstPhoto };
        return next;
      });
    });
    setBulkSwapSelection(null);
  };
  const save = async () => {
    if (captureMode === 'bulk') {
      if (!bulkGroups.length) return bulkRef.current?.click();
      const missingFrontIndex = bulkGroups.findIndex(group => !group.front);
      if (missingFrontIndex >= 0) {
        setPhotoError(`Item ${missingFrontIndex + 1} needs a FRONT photo before saving.`);
        return;
      }
      setSaving(true);
      const { locationId, zoneId } = ensureIntake();
      const references = bulkGroups.map(group => {
        const item = Store.addItem(locationId, zoneId, group.front);
        Store.updateItem(locationId, zoneId, item.id, {
          label: group.label.trim(),
          backImageData: group.back,
          tagImageData: group.tag,
          workwear,
          intent,
          aiScanStatus: scanAfterSave && autoAllowed ? 'queued' : 'not_scanned',
          serverSyncStatus: aiSession.token ? 'queued' : 'local_only',
          details: { profileType, category: '', brand: '', size: '', color: '', material: '', care: '', season: '' },
        });
        return { locationId, zoneId, itemId: item.id, scan: scanAfterSave && autoAllowed, sync: !!aiSession.token };
      });
      setBulkGroups([]);
      setBulkSwapSelection(null);
      setWorkwear(false);
      setProfileType('clothing');
      setIntent({ wear: false, sell: false });
      setSaving(false);
      onSaved(references);
      return;
    }
    if (!imageData) return libraryRef.current?.click();
    setSaving(true);
    const { locationId, zoneId } = ensureIntake();
    const item = Store.addItem(locationId, zoneId, imageData);
    Store.updateItem(locationId, zoneId, item.id, {
      label: label.trim(),
      backImageData,
      tagImageData,
      workwear,
      intent,
      aiScanStatus: scanAfterSave && autoAllowed ? 'queued' : 'not_scanned',
      serverSyncStatus: aiSession.token ? 'queued' : 'local_only',
      details: { profileType, category: '', brand: '', size: '', color: '', material: '', care: '', season: '' },
    });
    setImageData('');
    setBackImageData('');
    setTagImageData('');
    setWorkwear(false);
    setProfileType('clothing');
    setLabel('');
    setIntent({ wear: false, sell: false });
    setSaving(false);
    onSaved({ locationId, zoneId, itemId: item.id, scan: scanAfterSave && autoAllowed, sync: !!aiSession.token });
  };
  if (!open) return null;
  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <section className="capture-sheet" role="dialog" aria-modal="true" aria-labelledby="capture-title">
        <div className="sheet-handle" />
        <div className="sheet-title"><div><span>Quick intake</span><h2 id="capture-title">Capture now.<br />Curate later.</h2></div><button onClick={onClose} aria-label="Close"><X /></button></div>
        <div className="capture-mode-toggle" role="group" aria-label="Capture mode"><button type="button" className={captureMode === 'single' ? 'active' : ''} aria-pressed={captureMode === 'single'} onClick={() => setCaptureMode('single')}><Camera /> Single item</button><button type="button" className={captureMode === 'bulk' ? 'active' : ''} aria-pressed={captureMode === 'bulk'} onClick={() => setCaptureMode('bulk')}><Images /> Bulk FRONT · BACK · TAG</button></div>
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={choose} />
        <input ref={libraryRef} type="file" accept="image/*" hidden onChange={choose} />
        <input ref={backRef} type="file" accept="image/*" hidden onChange={chooseBack} />
        <input ref={tagRef} type="file" accept="image/*" hidden onChange={chooseTag} />
        <input ref={bulkRef} type="file" accept="image/*" multiple hidden onChange={chooseBulk} />
        {captureMode === 'bulk' ? <section className="bulk-intake-review" aria-label="Bulk item photo review">
          <header><div><span>One reel opening</span><strong>Choose once in repeating order: FRONT → BACK → TAG</strong><small>Every card confirms the set. Tap any two slots to correct the order.</small></div><button type="button" onClick={() => bulkRef.current?.click()} disabled={bulkBusy}><Images /> {bulkBusy ? 'Preparing…' : bulkGroups.length ? 'Replace batch' : 'Choose photos once'}</button></header>
          {bulkGroups.length ? <div className="bulk-intake-groups">{bulkGroups.map((group, index) => {
            const photoCount = BULK_PHOTO_ROLES.reduce((count, role) => count + (group[role.key] ? 1 : 0), 0);
            return <article className={!group.front ? 'needs-front' : ''} key={group.id}>
            <div className="bulk-group-number"><span>Item {String(index + 1).padStart(2, '0')}</span><em className={photoCount === 3 ? 'complete' : ''}>{photoCount}/3 checked</em><button type="button" onClick={() => removeBulkGroup(group.id)} aria-label={`Remove item set ${index + 1}`}><X /></button></div>
            <div className="bulk-role-grid" aria-label={`Front, back, and tag photos for item ${index + 1}`}>{BULK_PHOTO_ROLES.map(({ key: role, label: roleLabel }) => {
              const source = group[role];
              const selected = bulkSwapSelection?.groupId === group.id && bulkSwapSelection.role === role;
              return <button type="button" className={`${source ? 'has-image' : ''} ${selected ? 'selected' : ''}`} aria-pressed={selected} aria-label={`${source ? roleLabel : `Empty ${roleLabel}`} slot for item ${index + 1}. ${selected ? 'Selected; choose another slot to swap.' : 'Select to swap roles.'}`} onClick={() => selectBulkPhotoSlot(group.id, role)} key={role}>{source ? <img src={source} alt={`${roleLabel.toLowerCase()} preview for item ${index + 1}`} /> : <span><Plus /></span>}<i>{roleLabel}</i></button>;
            })}</div>
            <small className="bulk-swap-hint">{bulkSwapSelection?.groupId === group.id ? 'Selected—tap another slot in any card to swap.' : !group.front ? 'A front photo is required. Tap this empty slot, then the correct photo.' : 'Tap any two photos to swap their roles.'}</small>
            <label><span>Item name <small>optional</small></span><input value={group.label} onChange={event => updateBulkLabel(group.id, event.target.value)} placeholder={`Item ${index + 1}`} /></label>
          </article>})}</div> : <button type="button" className="bulk-intake-empty" onClick={() => bulkRef.current?.click()} disabled={bulkBusy}><Images /><span><strong>{bulkBusy ? 'Preparing your batch…' : 'Choose all angles in one pass'}</strong><small>1 FRONT, 2 BACK, 3 TAG—then repeat for the next item.</small></span><ArrowRight /></button>}
        </section> : imageData ? <button className="capture-preview" onClick={() => libraryRef.current?.click()}><MirrorLens label="Open Reflector as a private live mirror"><img src={imageData} alt="New item preview" /></MirrorLens><span>Choose another</span></button> : <div className="capture-sources"><button onClick={() => cameraRef.current?.click()}><Camera /><strong>Take photo</strong><small>Open the camera</small></button><button onClick={() => libraryRef.current?.click()}><Images /><strong>Photo library</strong><small>Import what you have</small></button></div>}
        <div className="reflector-capture-boundary"><ShieldCheck /><span><strong>Reflector™ camera boundary</strong><small>Permission is explicit, microphone access is blocked, and the live mirror is never recorded. Saved captures follow your sync setting.</small></span></div>
        {photoError ? <p className="photo-error" role="alert">{photoError}</p> : null}
        {captureMode === 'single' ? <label className="label-field"><span>Name it now <small>optional</small></span><input value={label} onChange={event => setLabel(event.target.value)} placeholder="e.g. Silver knit" /></label> : null}
        <div className="profile-type-picker" role="group" aria-label="Record profile"><span>Track as</span><div>{[['clothing', 'Clothing'], ['accessory', 'Accessory'], ['hair_beauty', 'Hair & beauty'], ['personal_care', 'Personal care']].map(([value, text]) => <button type="button" key={value} className={profileType === value ? 'active' : ''} aria-pressed={profileType === value} onClick={() => setProfileType(value)}>{text}</button>)}</div><small>Accessories and care records stay searchable beside clothing without pretending they are garments.</small></div>
        {captureMode === 'single' ? <><button className={`tag-photo-button ${backImageData ? 'active' : ''}`} onClick={() => backRef.current?.click()}><Images /><span><strong>{backImageData ? 'Back photo attached' : 'Add back photo'}</strong><small>Optional · helps remember details from both sides</small></span>{backImageData ? <CheckCircle2 /> : <Upload />}</button>
        <button className={`tag-photo-button ${tagImageData ? 'active' : ''}`} onClick={() => tagRef.current?.click()}><Tag /><span><strong>{tagImageData ? 'Care label attached' : 'Add care-label photo'}</strong><small>Optional · improves brand, size, fabric and care reading</small></span>{tagImageData ? <CheckCircle2 /> : <Upload />}</button></> : null}
        <div className="capture-intent" role="group" aria-label="Item lists">
          <button aria-pressed={intent.wear} className={intent.wear ? 'active' : ''} onClick={() => setIntent(current => ({ ...current, wear: !current.wear }))}><Heart /><span><strong>Wear</strong><small>Keep in rotation</small></span></button>
          <button aria-pressed={intent.sell} className={intent.sell ? 'active' : ''} onClick={() => setIntent(current => ({ ...current, sell: !current.sell }))}><Tag /><span><strong>Sell</strong><small>Prepare to list</small></span></button>
          <button aria-pressed={workwear} className={`workwear-toggle ${workwear ? 'active' : ''}`} onClick={() => setWorkwear(value => !value)}><Shirt /><span><strong>Work clothes</strong><small>Show in morning search</small></span></button>
        </div>
        <button className={`scan-toggle ${scanAfterSave && autoAllowed ? 'active' : ''}`} aria-pressed={scanAfterSave && autoAllowed} aria-disabled={!autoAllowed} onClick={() => autoAllowed && setScanAfterSave(value => !value)}><ScanLine /><span><strong>{!aiSession.token ? 'Sign in to use MIЯЯOЯ AI' : autoAllowed ? 'AI scan on upload' : 'Manual AI scans only'}</strong><small>{!aiSession.token ? 'Connect your account from Profile' : autoAllowed ? 'Premium auto-fills tags, descriptors and resale estimate' : aiSession.quota?.ai?.trial ? 'Trial · one Try-On per day' : `Verified User · ${captureThreads}${aiSession.quota?.ai?.promotion ? ' · 3× this month' : ''}`}</small></span><i>{autoAllowed ? (scanAfterSave ? 'On' : 'Off') : aiSession.token ? 'Manual' : 'Off'}</i></button>
        <div className="intake-explainer"><Check /><p><strong>Saved first to Unsorted Intake</strong><br />Your photo is safe even if AI is offline or needs review.</p></div>
        <button className="save-capture" disabled={saving || bulkBusy || (captureMode === 'bulk' && bulkGroups.some(group => !group.front))} onClick={save}>{saving ? 'Saving…' : captureMode === 'bulk' && bulkGroups.length ? `Save ${bulkGroups.length} ${bulkGroups.length === 1 ? 'item' : 'items'} to intake` : 'Save to intake'} <ArrowRight /></button>
      </section>
    </div>
  );
}

function isTryOnEligible(item) {
  if (item?.location?.type === 'surrounding') return false;
  const category = String(item?.details?.category || '').toLowerCase();
  return !/(tool|document|book|furniture|decor|appliance|electronic)/.test(category);
}

function profileTypeLabel(value) {
  return ({ clothing: 'Clothing', accessory: 'Accessory', hair_beauty: 'Hair & beauty', personal_care: 'Personal care' })[value] || 'Clothing';
}

function formatTopicDate(value) {
  if (!value) return 'Not set';
  const date = new Date(`${value}T00:00:00`);
  return Number.isFinite(date.getTime()) ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : value;
}

function getSurroundingTopic(item) {
  const detail = item?.surrounding || {};
  const branch = detail.branch || 'objects';
  const value = (field, fallback = 'Not set') => detail[field] || fallback;
  const nextWater = branch === 'garden' && detail.lastWateredAt && Number(detail.wateringEveryDays) > 0
    ? (() => {
        const date = new Date(`${detail.lastWateredAt}T00:00:00`);
        date.setDate(date.getDate() + Number(detail.wateringEveryDays));
        return Number.isFinite(date.getTime()) ? date.toISOString().slice(0, 10) : '';
      })()
    : '';
  const topics = {
    garden: { eyebrow: 'Garden focus', title: 'Plant care', rows: [['Last watered', formatTopicDate(detail.lastWateredAt)], ['Watering rhythm', detail.wateringEveryDays ? `Every ${detail.wateringEveryDays} days` : 'Suggested by AI or set manually'], ['Next water', formatTopicDate(nextWater)]], note: value('care', value('infoText', 'Keep the plant record close to its light, water, and seasonal context.')) },
    library: { eyebrow: 'Library focus', title: 'Reading life', rows: [['Reading status', value('readingStatus')], ['Next attention', value('nextAction')], ['Review date', formatTopicDate(detail.reviewAt)]], note: value('infoText', value('notes', 'Mark a book read, reading, unread, loaned, or decor as its place changes.')) },
    storage: { eyebrow: 'Storage focus', title: 'Stored with intention', rows: [['Storage horizon', value('storageTerm')], ['Season', value('storageSeason')], ['Review date', formatTopicDate(detail.reviewAt)]], note: value('infoText', value('notes', 'Short-term, long-term, and seasonal records stay easier to find when the reason is visible.')) },
    pantry: { eyebrow: 'Pantry focus', title: 'Use what is here', rows: [['Use by', formatTopicDate(detail.expiryDate)], ['Opened', formatTopicDate(detail.openedAt)], ['Next attention', value('nextAction')]], note: value('infoText', value('notes', 'Keep expiry and opened dates visible so groceries can move with intention.')) },
    care: { eyebrow: 'Care focus', title: 'Instructions at hand', rows: [['Next attention', value('nextAction')], ['Review date', formatTopicDate(detail.reviewAt)], ['Status', value('status')]], note: value('infoText', value('care', value('notes', 'Keep manuals, warranties, labels, and instructions attached to the thing they serve.'))) },
    objects: { eyebrow: 'Objects focus', title: 'A place for the useful', rows: [['Next attention', value('nextAction')], ['Status', value('status')], ['Kind', value('kind')]], note: value('infoText', value('notes', 'Give objects enough context to remain useful, beautiful, or easy to release.')) },
  };
  return topics[branch] || topics.objects;
}

function ItemDetailSheet({ entry, state, onClose, refresh, aiSession, onScan, scanning, onTryOn, cooldownState, showToast, onRecreateOotd, onOpenItem }) {
  const [moveOpen, setMoveOpen] = useState(false);
  const [destinationCollectionId, setDestinationCollectionId] = useState('');
  const [destinationLocationId, setDestinationLocationId] = useState('');
  const [destinationZoneId, setDestinationZoneId] = useState('');
  const [includeSurroundingDestinations, setIncludeSurroundingDestinations] = useState(false);
  const [destinationSurroundingBranch, setDestinationSurroundingBranch] = useState('objects');
  const [editOpen, setEditOpen] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [pendingWaterDate, setPendingWaterDate] = useState('');
  const [tagBusy, setTagBusy] = useState(false);
  const [backBusy, setBackBusy] = useState(false);
  const [careBusy, setCareBusy] = useState(false);
  const [readingReport, setReadingReport] = useState({ status: 'unread', page: '', total: '', chapter: '', note: '' });
  const [readingConsent, setReadingConsent] = useState(false);
  const [readingBusy, setReadingBusy] = useState(false);
  const tagRef = useRef(null);
  const backRef = useRef(null);
  const careRef = useRef(null);
  useEffect(() => { setPendingWaterDate(entry?.surrounding?.lastWateredAt || ''); }, [entry?.id, entry?.surrounding?.lastWateredAt]);
  useEffect(() => {
    const detail = normalizeSurroundingDetails(entry?.surrounding);
    setReadingReport({ status: detail.readingStatus, page: detail.currentPage, total: detail.totalPages, chapter: detail.currentChapter, note: detail.readingNotes });
    setReadingConsent(false);
  }, [entry?.id, entry?.surrounding]);
  const defaultDestinationCollectionId = state.activeCollectionId || state.collections?.[0]?.id || '';
  const surroundingCollection = state.collections?.find(collection => collection.vibe === 'surrounding') || null;
  const defaultSurroundingBranch = entry?.surrounding?.branch || (state.preferences?.defaultSurroundingBranch && state.preferences.defaultSurroundingBranch !== 'all' ? state.preferences.defaultSurroundingBranch : 'objects');
  useEffect(() => {
    if (!entry?.id) return;
    const startsInSurroundings = entry.location?.type === 'surrounding' && surroundingCollection?.id;
    setMoveOpen(false);
    setDestinationCollectionId(startsInSurroundings ? surroundingCollection.id : defaultDestinationCollectionId);
    setDestinationLocationId('');
    setDestinationZoneId('');
    setIncludeSurroundingDestinations(!!startsInSurroundings);
    setDestinationSurroundingBranch(defaultSurroundingBranch);
  }, [defaultDestinationCollectionId, defaultSurroundingBranch, entry?.id, entry?.location?.type, surroundingCollection?.id]);
  useEffect(() => {
    const collectionId = destinationCollectionId || state.activeCollectionId || state.collections?.[0]?.id;
    const collection = state.collections?.find(candidate => candidate.id === collectionId);
    const isSurroundingCollection = collection?.vibe === 'surrounding';
    const nextLocations = (state.locations || []).filter(location => location.collectionId === collectionId && !isIntakeLocation(location) && (isSurroundingCollection || includeSurroundingDestinations || location.type !== 'surrounding') && location.zones?.length);
    const liveLocation = nextLocations.find(location => location.id === destinationLocationId)
      || (isSurroundingCollection ? nextLocations.find(location => location.focusBranch === destinationSurroundingBranch) : null)
      || nextLocations[0]
      || null;
    const liveZone = liveLocation?.zones?.find(zone => zone.id === destinationZoneId) || liveLocation?.zones?.[0] || null;
    if (liveLocation?.id !== destinationLocationId) setDestinationLocationId(liveLocation?.id || '');
    if (liveZone?.id !== destinationZoneId) setDestinationZoneId(liveZone?.id || '');
  }, [destinationCollectionId, destinationLocationId, destinationSurroundingBranch, destinationZoneId, includeSurroundingDestinations, state.activeCollectionId, state.collections, state.locations]);
  if (!entry) return null;
  const item = entry;
  const isSurroundingItem = item?.location?.type === 'surrounding';
  const destinationCollection = (state.collections || []).find(collection => collection.id === (destinationCollectionId || defaultDestinationCollectionId)) || (state.collections || [])[0] || null;
  const movingToSurroundings = destinationCollection?.vibe === 'surrounding';
  const destinations = (state.locations || []).filter(location => location.collectionId === destinationCollection?.id && !isIntakeLocation(location) && (movingToSurroundings || includeSurroundingDestinations || location.type !== 'surrounding') && location.zones?.length);
  const destinationLocation = destinations.find(location => location.id === destinationLocationId) || destinations[0] || null;
  const destinationZone = destinationLocation?.zones?.find(zone => zone.id === destinationZoneId) || destinationLocation?.zones?.[0] || null;
  const market = item.aiMarket || {};
  const estimate = market.aftermarketValue || market.currentMarketValue || 'Not estimated';
  const recommendedPlatform = market.recommendedPlatform || 'Scan for a suggestion';
  const surroundingTopic = isSurroundingItem ? getSurroundingTopic(item) : null;
  const intelligence = isSurroundingItem ? [
    ['Branch', item.surrounding?.branch || 'Objects'],
    ['Kind', getSurroundingKind(item)],
    ['Status', item.surrounding?.status || 'Active'],
    ['Next', item.surrounding?.nextAction || 'Not set'],
  ] : [
    ['Profile', profileTypeLabel(item.details?.profileType)],
    ['Category', item.details?.category || 'Not set'],
    ['Color', item.details?.color || 'Not set'],
    ['Material', item.details?.material || 'Not set'],
    ['Season', item.details?.season || 'Not set'],
  ];
  const toggleIntent = key => {
    Store.updateItem(entry.location.id, entry.zone.id, entry.id, { intent: { ...(entry.intent || {}), [key]: !entry.intent?.[key] } });
    refresh();
  };
  const toggleWorkwear = () => {
    Store.updateItem(entry.location.id, entry.zone.id, entry.id, { workwear: !item.workwear });
    refresh();
  };
  const togglePublicMirror = async () => {
    if (!aiSession.token) {
      showToast?.('Sign in before placing an item in your Public Mirror.');
      return;
    }
    try {
      let imageMediaId = item.imageMediaId || '';
      let imageSha256 = item.imageSha256 || '';
      if (!item.publicMirror && !imageMediaId && item.imageData) {
        const upload = await uploadMirrorImage(item.imageData, 'public-mirror');
        imageMediaId = upload.mediaId || '';
        imageSha256 = upload.sha256 || imageSha256;
      }
      Store.updateItem(entry.location.id, entry.zone.id, entry.id, {
        publicMirror: !item.publicMirror,
        ...(imageMediaId ? { imageMediaId, imageSha256 } : {}),
      });
      refresh();
      const publicItems = flattenState(Store.getState())
        .filter(candidate => candidate.publicMirror && !isIntakeLocation(candidate.location) && candidate.location?.type !== 'surrounding')
        .map(candidate => ({ itemKey: String(candidate.id), label: candidate.label || 'Untitled piece', category: candidate.details?.category || candidate.details?.profileType || 'Clothing', note: [candidate.details?.color, candidate.details?.season].filter(Boolean).join(' · '), mediaId: candidate.imageMediaId || '' }));
      await Promise.all((aiSession.parties || []).map(party => replaceMirrorPartyMirror(party.id, publicItems).catch(() => null)));
      showToast?.(!item.publicMirror ? 'Added to your Public Mirror for sync parties.' : 'Removed from your Public Mirror.');
    } catch (error) {
      showToast?.(error.message || 'That item could not be prepared for the Public Mirror.');
    }
  };
  const setWearStatus = wearStatus => {
    Store.setItemsWearStatus([item.id], wearStatus);
    refresh();
    showToast?.(wearStatus === 'clean' ? 'Item is back in rotation.' : wearStatus === 'laundry' ? 'Item moved to laundry.' : 'Item marked worn.');
  };
  const approvePendingAi = () => {
    if (!item.pendingAiSuggestion) return;
    const merged = mergeAiSuggestion(item, item.pendingAiSuggestion);
    if (pendingWaterDate && merged.surrounding?.branch === 'garden') merged.surrounding = { ...(merged.surrounding || {}), lastWateredAt: pendingWaterDate };
    Store.updateItem(entry.location.id, entry.zone.id, entry.id, { ...merged, pendingAiSuggestion: null, aiNeedsReview: false, aiNotes: 'AI suggestion approved.' });
    refresh();
    showToast?.('AI details approved and added to this record.');
  };
  const keepManualDetails = () => {
    Store.updateItem(entry.location.id, entry.zone.id, entry.id, { pendingAiSuggestion: null, aiNeedsReview: false, aiNotes: 'AI suggestion dismissed; manual details kept.' });
    refresh();
    showToast?.('Manual details kept.');
  };
  const toggleSurroundingTracking = () => {
    if (!isSurroundingItem) return;
    const tracked = !item.surrounding?.tracked;
    Store.updateItem(entry.location.id, entry.zone.id, entry.id, {
      surrounding: {
        ...(item.surrounding || {}),
        tracked,
        trackedAt: tracked ? new Date().toISOString().slice(0, 10) : '',
        trackingEveryDays: tracked ? (item.surrounding?.trackingEveryDays || '7') : '',
      },
    });
    refresh();
    showToast?.(tracked ? 'Record added to your reflective schedule.' : 'Record removed from reflective tracking.');
  };
  const saveReadingReport = event => {
    event.preventDefault();
    if (!isSurroundingItem || item.surrounding?.branch !== 'library') return;
    Store.updateItem(entry.location.id, entry.zone.id, entry.id, {
      surrounding: {
        ...(item.surrounding || {}),
        readingStatus: readingReport.status,
        currentPage: readingReport.page,
        totalPages: readingReport.total,
        currentChapter: readingReport.chapter,
        readingNotes: readingReport.note,
      },
    });
    refresh();
    showToast?.('Reading report saved to this book.');
  };
  const reflectOnReading = async () => {
    const report = readingReport.note.trim();
    if (!readingConsent || !report || readingBusy) return;
    if (!aiSession.token) {
      showToast?.('Sign in from Profile to use an AI reflection.');
      return;
    }
    setReadingBusy(true);
    try {
      const detail = normalizeSurroundingDetails(item.surrounding);
      const context = [`Reading: ${item.label || 'Untitled book'}`, detail.author ? `Author: ${detail.author}` : '', readingReport.chapter ? `Paused at: ${readingReport.chapter}` : '', `Reader report: ${report}`].filter(Boolean).join('\n');
      const result = await reflectWitnessNote({ note: context, weather: '', agenda: [] });
      Store.saveWitnessEntry({ note: `${item.label || 'Book'} · ${report}`, reflection: result.reflection, summary: result.summary, kind: 'reading', weather: '' });
      showToast?.('Your reading reflection is saved in Witness.');
      setReadingConsent(false);
    } catch (error) {
      showToast?.(error.message || 'Reading reflection could not start yet.');
    } finally {
      setReadingBusy(false);
    }
  };
  const chooseTagPhoto = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setTagBusy(true);
    try {
      const tagImageData = await fileToOptimizedImage(file, { maxDimension: 1200, minDimension: 720, targetBytes: 500 * 1024 });
      Store.updateItem(entry.location.id, entry.zone.id, entry.id, {
        tagImageData,
        tagImageMediaId: '',
        tagImageSha256: '',
        serverSyncStatus: aiSession.token ? 'queued' : item.serverSyncStatus,
      });
      refresh();
      showToast?.('Tag photo attached. Scan this item to apply its details.');
    } catch (error) {
      showToast?.(error.message || 'That tag photo could not be prepared.');
    } finally {
      setTagBusy(false);
    }
  };
  const chooseBackPhoto = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBackBusy(true);
    try {
      const backImageData = await fileToOptimizedImage(file);
      Store.updateItem(entry.location.id, entry.zone.id, entry.id, {
        backImageData,
        backImageMediaId: '',
        backImageSha256: '',
        serverSyncStatus: aiSession.token ? 'queued' : item.serverSyncStatus,
      });
      refresh();
      showToast?.('Back photo attached.');
    } catch (error) {
      showToast?.(error.message || 'That back photo could not be prepared.');
    } finally {
      setBackBusy(false);
    }
  };
  const chooseCarePhoto = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setCareBusy(true);
    try {
      const careImageData = await fileToOptimizedImage(file, { maxDimension: 1400, minDimension: 720, targetBytes: 600 * 1024 });
      Store.updateItem(entry.location.id, entry.zone.id, entry.id, {
        careImageData,
        careImageMediaId: '',
        careImageSha256: '',
        serverSyncStatus: aiSession.token ? 'queued' : item.serverSyncStatus,
      });
      refresh();
      if (aiSession.token) {
        onScan?.(entry, 'surrounding_info_scan', { autoScan: false, approvalRequired: true });
        showToast?.('Care photo attached. AI will prepare an update for your approval.');
      } else {
        showToast?.('Care photo attached. Connect AI if you want care notes refreshed.');
      }
    } catch (error) {
      showToast?.(error.message || 'That care photo could not be prepared.');
    } finally {
      setCareBusy(false);
    }
  };
  const removeItem = reason => {
    const label = item.label || 'Item';
    Store.deleteItem(entry.location.id, entry.zone.id, entry.id, reason);
    refresh();
    onClose();
    showToast?.(`${label} marked ${reason} and removed.`);
  };
  const openEditor = () => {
    setEditDraft({
      label: item.label || '',
      profileType: item.details?.profileType || 'clothing',
      brand: item.details?.brand || '',
      category: item.details?.category || '',
      color: item.details?.color || '',
      material: item.details?.material || '',
      season: item.details?.season || '',
      size: item.details?.size || '',
      care: item.details?.care || '',
      tags: (item.tags || []).join(', '),
      surrounding: { ...(item.surrounding || {}) },
    });
    setEditOpen(true);
  };
  const saveEditor = () => {
    Store.updateItem(entry.location.id, entry.zone.id, entry.id, {
      label: editDraft.label.trim(),
      details: {
        ...(item.details || {}),
        profileType: editDraft.profileType,
        brand: editDraft.brand.trim(),
        category: editDraft.category.trim(),
        color: editDraft.color.trim(),
        material: editDraft.material.trim(),
        season: editDraft.season.trim(),
        size: editDraft.size.trim(),
        care: editDraft.care.trim(),
      },
      tags: editDraft.tags.split(',').map(value => value.trim()).filter(Boolean),
      ...(isSurroundingItem ? { surrounding: { ...(item.surrounding || {}), ...(editDraft.surrounding || {}) } } : {}),
    });
    refresh();
    setEditOpen(false);
  };
  const setEditSurrounding = (key, value) => setEditDraft(current => ({ ...current, surrounding: { ...(current?.surrounding || {}), [key]: value } }));
  const moveItem = () => {
    if (!destinationLocation || !destinationZone) return;
    Store.reassignItemToZone(entry.location.id, entry.zone.id, destinationLocation.id, destinationZone.id, entry.id);
    if (movingToSurroundings) {
      const branch = destinationSurroundingBranch || 'objects';
      const kind = item.surrounding?.kind && SURROUNDING_KIND_OPTIONS[branch]?.includes(item.surrounding.kind)
        ? item.surrounding.kind
        : SURROUNDING_KIND_OPTIONS[branch]?.[0] || 'Object';
      Store.updateItem(destinationLocation.id, destinationZone.id, entry.id, {
        details: { ...(item.details || {}), category: kind },
        intent: { ...(item.intent || {}), wear: false },
        surrounding: { ...(item.surrounding || {}), branch, kind, status: item.surrounding?.status || 'active' },
      });
    }
    refresh();
    setMoveOpen(false);
    onClose();
  };
  const createSurroundingStationAndMove = () => {
    let space = surroundingCollection;
    if (!space) {
      space = Store.addCollection('Surroundings', 'surrounding');
      if (defaultDestinationCollectionId) Store.setActiveCollection(defaultDestinationCollectionId);
    }
    if (!space) return;
    const branch = destinationSurroundingBranch || 'objects';
    const branchMeta = SURROUNDING_BRANCHES.find(candidate => candidate.id === branch) || SURROUNDING_BRANCHES.find(candidate => candidate.id === 'objects');
    const theme = SURROUNDING_THEMES.find(candidate => candidate.branch === branch) || SURROUNDING_THEMES.find(candidate => candidate.branch === 'objects');
    const location = Store.addLocation(`${branchMeta?.shortLabel || 'Surrounding'} station`, 'surrounding', space.id);
    Store.updateLocationFocus(location.id, { focusTheme: theme?.id || branch, focusBranch: branch, focusColor: theme?.color || '#8fb8ef', focusSlogan: theme?.slogan || branchMeta?.description || '' });
    const zone = Store.addZone(location.id, 'Unsorted');
    if (!zone) return;
    setIncludeSurroundingDestinations(true);
    setDestinationCollectionId(space.id);
    setDestinationLocationId(location.id);
    setDestinationZoneId(zone.id);
    refresh();
    setMoveOpen(true);
    showToast?.(`${branchMeta?.shortLabel || 'Surrounding'} station created. Review the section, then choose Move here.`);
  };
  const createClosetAndMove = () => {
    if (!destinationCollection) return;
    const location = Store.addLocation('Drop-in closet', 'closet', destinationCollection.id);
    Store.updateLocationPurpose(location.id, entry.intent?.sell && !entry.intent?.wear ? 'sell' : 'home');
    const zone = Store.addZone(location.id, 'Drop-in');
    if (!zone) return;
    setDestinationCollectionId(destinationCollection.id);
    setDestinationLocationId(location.id);
    setDestinationZoneId(zone.id);
    refresh();
    setMoveOpen(true);
    showToast?.('Drop-in section created. Review the destination, then choose Move here.');
  };
  const links = marketplaceSearchLinks(item);
  const scanStatus = scanning || item.aiScanStatus === 'scanning'
    ? 'AI is reading this piece…'
    : item.aiScanStatus === 'failed'
      ? item.aiScanError || 'AI scan needs another try'
      : item.aiMarket?.updatedAt
        ? `AI estimate · ${item.aiConfidence || 0}% confidence`
        : 'Not scanned yet';
  const scanCoolingDown = isCooldownFor(cooldownState, 'ai_scan');
  const editBranch = editDraft?.surrounding?.branch || item.surrounding?.branch || 'objects';
  const editKinds = SURROUNDING_KIND_OPTIONS[editBranch] || SURROUNDING_KIND_OPTIONS.objects;
  const itemFits = (state.mirror?.ootdHistory || []).filter(history => (history.itemIds || []).map(String).includes(String(item.id)));
  const companionCounts = new Map();
  itemFits.forEach(history => (history.itemIds || []).forEach(id => {
    const key = String(id);
    if (key !== String(item.id)) companionCounts.set(key, (companionCounts.get(key) || 0) + 1);
  }));
  const topCompanion = [...companionCounts.entries()].sort((a, b) => b[1] - a[1])[0];
  const topCompanionItem = topCompanion ? flattenState(state).find(candidate => String(candidate.id) === topCompanion[0]) : null;
  const lastWornLabel = itemFits[0]?.createdAt
    ? new Date(itemFits[0].createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Not yet';

  return (
    <div className="detail-layer" role="dialog" aria-modal="true" aria-labelledby="item-detail-title">
      <main className="item-detail">
        <header className="detail-header"><button onClick={onClose}><ArrowLeft /> Closet</button><BrandMark /><span /></header>
        <div className="detail-workspace">
          <div className="detail-media-column">
            <div className="detail-photo"><PrivateImage source={item.imageData} mediaId={item.imageMediaId} fallback="./assets/ink-trench.webp" alt={item.label || 'Wardrobe item'} /><button onClick={openEditor} aria-label="Edit item metadata"><Tag /></button></div>
            <section className="detail-intro">
              <h1 id="item-detail-title">{item.label || 'Untitled piece'}</h1>
              <p>{collectionName(state, item.location.collectionId)} · {item.location.name} · {item.zone.name}</p>
              <div className={`scan-status ${item.aiScanStatus === 'failed' ? 'error' : ''}`}><ScanLine /> {scanStatus}</div>
              {item.serverSyncStatus && item.serverSyncStatus !== 'local_only' ? <div className={`sync-status ${item.serverSyncStatus === 'failed' ? 'error' : ''}`}><HardDrive /> {item.serverSyncStatus === 'complete' ? 'Compressed photo synced to VPS' : item.serverSyncStatus === 'syncing' || item.serverSyncStatus === 'queued' ? 'Syncing compressed photo to VPS…' : 'Photo remains safe locally; VPS sync needs another try'}</div> : null}
            </section>
            {!isSurroundingItem ? <details className="detail-media-drawer">
              <summary><span><small>Photo record</small><strong>Front · Back · Tag</strong></span><ChevronDown /></summary>
              <div className="detail-media-grid">
                <figure><PrivateImage source={item.imageData} mediaId={item.imageMediaId} fallback="./assets/ink-trench.webp" alt="Front of item" /><figcaption>Front</figcaption></figure>
                <figure><span className="detail-media-empty">{item.backImageData || item.backImageMediaId ? <PrivateImage source={item.backImageData} mediaId={item.backImageMediaId} alt="Back of item" /> : <Images />}</span><figcaption>Back</figcaption></figure>
                <figure><span className="detail-media-empty">{item.tagImageData || item.tagImageMediaId ? <PrivateImage source={item.tagImageData} mediaId={item.tagImageMediaId} alt="Item tag" /> : <Tag />}</span><figcaption>Tag</figcaption></figure>
              </div>
              <div className="detail-media-actions">
                <input ref={backRef} type="file" accept="image/*" hidden onChange={chooseBackPhoto} />
                <input ref={tagRef} type="file" accept="image/*" hidden onChange={chooseTagPhoto} />
                <button className="tag-photo-action" onClick={() => backRef.current?.click()} disabled={backBusy}><Upload /> {backBusy ? 'Preparing…' : item.backImageData || item.backImageMediaId ? 'Replace back photo' : 'Upload back photo'}</button>
                <button className="tag-photo-action" onClick={() => tagRef.current?.click()} disabled={tagBusy}><Upload /> {tagBusy ? 'Preparing…' : item.tagImageData || item.tagImageMediaId ? 'Replace tag photo' : 'Upload tag photo'}</button>
              </div>
              <p className="detail-media-note">Front is the primary item photo. Back and tag images stay optional, but give the AI more context for fit, fabric, brand, size, and care.</p>
            </details> : null}
          </div>
          <div className="detail-info-column">
        <section className={`intent-actions ${isSurroundingItem ? 'surrounding-intent-actions' : ''}`} aria-label="Wear, sell, and work clothes lists">
          {!isSurroundingItem ? <button aria-pressed={!!item.intent?.wear} className={item.intent?.wear ? 'active' : ''} onClick={() => toggleIntent('wear')}><Heart /><span>Wear list</span></button> : null}
          <button aria-pressed={!!item.intent?.sell} className={item.intent?.sell ? 'active' : ''} onClick={() => toggleIntent('sell')}><Tag /><span>Sell list</span></button>
          {!isSurroundingItem ? <button aria-pressed={!!item.publicMirror} className={item.publicMirror ? 'active' : ''} onClick={togglePublicMirror}><UsersRound /><span>Public Mirror</span></button> : null}
          {!isSurroundingItem ? <button aria-pressed={!!item.workwear} className={item.workwear ? 'active' : ''} onClick={toggleWorkwear}><Shirt /><span>Work clothes</span></button> : null}
          <p>{isSurroundingItem ? 'Any Surrounding record can be evaluated for sale.' : 'Public Mirror is the small set of pieces friends can see in a sync party. Keep everything else private.'}</p>
        </section>

        {!isSurroundingItem ? <section className="wear-cycle-section" aria-labelledby="wear-cycle-title">
          <div><span>Wear cycle</span><h2 id="wear-cycle-title">{item.wearStatus === 'laundry' ? 'In laundry' : item.wearStatus === 'worn' ? 'Worn once' : 'Clean and ready'}</h2></div>
          <p>Marking an item worn fades it in your closet until it returns clean.</p>
          <div className="wear-cycle-actions">
            {item.wearStatus === 'clean' ? <button onClick={() => setWearStatus('worn')}><Check /> Mark worn</button> : null}
            {item.wearStatus === 'worn' ? <button onClick={() => setWearStatus('laundry')}><RefreshCw /> Send to laundry</button> : null}
            {item.wearStatus === 'laundry' ? <button onClick={() => setWearStatus('clean')}><Check /> Mark clean</button> : null}
          </div>
        </section> : null}

        {isSurroundingItem ? <section className="surrounding-track-section" aria-labelledby="surrounding-track-title"><div><span>Reflective care</span><h2 id="surrounding-track-title">Track this record</h2><p>Let this plant, book, storage record, or object appear when it is due for attention.</p></div><label className="desktop-toggle"><input type="checkbox" checked={!!item.surrounding?.tracked} onChange={toggleSurroundingTracking} /><span /><strong>{item.surrounding?.tracked ? 'Tracking on' : 'Track on schedule'}</strong></label>{item.surrounding?.tracked ? <label className="tracking-cadence"><span>Every</span><select value={item.surrounding?.trackingEveryDays || '7'} onChange={event => { Store.updateItem(entry.location.id, entry.zone.id, entry.id, { surrounding: { ...(item.surrounding || {}), trackingEveryDays: event.target.value } }); refresh(); }}><option value="1">day</option><option value="7">week</option><option value="14">two weeks</option><option value="30">month</option></select></label> : null}</section> : null}

        {isSurroundingItem && item.surrounding?.branch === 'garden' ? <section className="plant-care-photo-section" aria-labelledby="plant-care-photo-title"><div><span>Plant identity &amp; care</span><h2 id="plant-care-photo-title">Show the latest growth.</h2><p>Ask AI to propose a plant identity and care context from a photo. Nothing changes until you approve the suggestion.</p></div>{item.careImageData || item.careImageMediaId ? <PrivateImage source={item.careImageData} mediaId={item.careImageMediaId} fallback="./assets/silver-knit.webp" alt="Latest plant care photo" /> : null}<input ref={careRef} type="file" accept="image/*" hidden onChange={chooseCarePhoto} /><div className="plant-care-actions"><button className="tag-photo-action" onClick={() => careRef.current?.click()} disabled={careBusy}><Upload /> {careBusy ? 'Preparing…' : item.careImageData || item.careImageMediaId ? 'Update plant photo' : 'Add plant photo'}</button>{aiSession.token ? <button className="tag-photo-action" disabled={scanning || scanCoolingDown} onClick={() => onScan(entry, 'surrounding_info_scan', { autoScan: false, approvalRequired: true })}><Sparkles /> {scanning ? 'Identifying…' : 'Identify plant'}</button> : null}</div></section> : null}

        {isSurroundingItem && item.surrounding?.branch === 'library' ? <section className="reading-report-section" aria-labelledby="reading-report-title"><div><span>Reading report</span><h2 id="reading-report-title">Where are you now?</h2><p>Kindle and other reader accounts are not connected. This is your private, manual reading record—page, chapter, and the thought you want to keep.</p></div><form onSubmit={saveReadingReport}><label><span>Status</span><select value={readingReport.status} onChange={event => setReadingReport(current => ({ ...current, status: event.target.value }))}><option value="unread">Unread</option><option value="reading">Reading</option><option value="read">Read</option><option value="shelved">Shelved</option><option value="loaned">Loaned out</option></select></label><label><span>Page</span><input type="number" min="0" value={readingReport.page} onChange={event => setReadingReport(current => ({ ...current, page: event.target.value }))} /></label><label><span>of</span><input type="number" min="0" value={readingReport.total} onChange={event => setReadingReport(current => ({ ...current, total: event.target.value }))} /></label><label className="reading-report-wide"><span>Chapter or section</span><input value={readingReport.chapter} onChange={event => setReadingReport(current => ({ ...current, chapter: event.target.value }))} placeholder="Where you paused" /></label><label className="reading-report-wide"><span>Your report</span><textarea value={readingReport.note} onChange={event => setReadingReport(current => ({ ...current, note: event.target.value }))} maxLength="2000" placeholder="What happened, what you noticed, or your own short excerpt" /></label><div className="reading-report-actions"><button type="submit"><Check /> Save report</button><label className="reading-ai-consent"><input type="checkbox" checked={readingConsent} onChange={event => setReadingConsent(event.target.checked)} /><span><strong>Use this report with AI</strong><small>Only the title, author, chapter, and text above are sent when you choose Reflect. Mirror does not access Kindle or fetch the book.</small></span></label><button type="button" disabled={!readingConsent || !readingReport.note.trim() || readingBusy} onClick={reflectOnReading}><Sparkles /> {readingBusy ? 'Reflecting…' : 'Reflect with AI'}</button></div></form></section> : null}

        {isTryOnEligible(item) ? <button className="item-tryon-action" onClick={() => onTryOn(entry)}><Sparkles /><span><strong>Try this on</strong><small>Open it in your private five-pose studio</small></span><ArrowRight /></button> : null}

        <section className="intelligence-section">
          <div className="detail-section-heading"><div><h2>{isSurroundingItem ? 'Object intelligence' : 'Item intelligence'}</h2>{isSurroundingItem && item.surrounding?.branch === 'garden' ? <span className="detail-section-kicker">Plant care stays review-first.</span> : null}</div><div className="detail-heading-actions"><button className="detail-edit-button" onClick={openEditor}><Tag /> Edit details</button>{aiSession.token ? <button disabled={scanning || scanCoolingDown} onClick={() => onScan(entry, isSurroundingItem ? 'surrounding_info_scan' : 'inventory_and_value_scan')}><RefreshCw /> {scanCoolingDown ? `Retry in ${formatCountdown(cooldownState.remainingSeconds)}` : item.aiMarket?.updatedAt ? 'Rescan' : isSurroundingItem ? 'Read info' : 'Scan'}</button> : null}</div></div>
          <div className="intelligence-list">{intelligence.map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}</div>
          {item.tags?.length ? <div className="tag-list">{item.tags.slice(0, 8).map(value => <span key={value}>{value}</span>)}</div> : null}
          {editOpen && editDraft ? <div className="metadata-editor">
            {!isSurroundingItem ? <label><span>Record profile</span><select value={editDraft.profileType} onChange={event => setEditDraft(current => ({ ...current, profileType: event.target.value }))}>{[['clothing', 'Clothing'], ['accessory', 'Accessory'], ['hair_beauty', 'Hair & beauty'], ['personal_care', 'Personal care']].map(([value, text]) => <option value={value} key={value}>{text}</option>)}</select></label> : null}
            {[
              ['label', 'Name'], ['brand', 'Brand'], ['category', 'Category'], ['color', 'Color'], ['material', 'Material'], ['season', 'Season'], ['size', 'Size'], ['care', 'Care'], ['tags', 'Tags, comma-separated'],
            ].map(([key, label]) => <label key={key}><span>{label}</span><input value={editDraft[key]} onChange={event => setEditDraft(current => ({ ...current, [key]: event.target.value }))} /></label>)}
            {isSurroundingItem ? <div className="surrounding-editor-fields"><label><span>Genre</span><select value={editBranch} onChange={event => { const nextBranch = event.target.value; setEditSurrounding('branch', nextBranch); setEditSurrounding('kind', SURROUNDING_KIND_OPTIONS[nextBranch]?.[0] || 'Object'); }}>{SURROUNDING_BRANCHES.map(branch => <option value={branch.id} key={branch.id}>{branch.label}</option>)}</select></label><label><span>Kind</span><select value={editDraft.surrounding.kind || ''} onChange={event => setEditSurrounding('kind', event.target.value)}>{editKinds.map(kind => <option value={kind} key={kind}>{kind}</option>)}</select></label>{editBranch === 'garden' ? <><label><span>Last watered</span><input type="date" value={editDraft.surrounding.lastWateredAt || ''} onChange={event => setEditSurrounding('lastWateredAt', event.target.value)} /></label><label><span>Water every (days)</span><input type="number" min="1" max="365" value={editDraft.surrounding.wateringEveryDays || ''} onChange={event => setEditSurrounding('wateringEveryDays', event.target.value)} /></label></> : null}{editBranch === 'library' ? <label><span>Reading status</span><select value={editDraft.surrounding.readingStatus || 'unread'} onChange={event => setEditSurrounding('readingStatus', event.target.value)}><option value="unread">Unread</option><option value="reading">Reading</option><option value="read">Read</option><option value="shelved">Shelved</option><option value="decor">Decor</option><option value="loaned">Loaned out</option></select></label> : null}{editBranch === 'storage' ? <><label><span>Storage horizon</span><select value={editDraft.surrounding.storageTerm || 'short'} onChange={event => setEditSurrounding('storageTerm', event.target.value)}><option value="short">Short-term</option><option value="long">Long-term</option><option value="seasonal">Seasonal</option></select></label><label><span>Season</span><input value={editDraft.surrounding.storageSeason || ''} onChange={event => setEditSurrounding('storageSeason', event.target.value)} /></label></> : null}{editBranch === 'pantry' ? <><label><span>Use by</span><input type="date" value={editDraft.surrounding.expiryDate || ''} onChange={event => setEditSurrounding('expiryDate', event.target.value)} /></label><label><span>Opened</span><input type="date" value={editDraft.surrounding.openedAt || ''} onChange={event => setEditSurrounding('openedAt', event.target.value)} /></label></> : null}<label><span>Next attention</span><input value={editDraft.surrounding.nextAction || ''} onChange={event => setEditSurrounding('nextAction', event.target.value)} /></label><label><span>Info / notes</span><textarea value={editDraft.surrounding.infoText || editDraft.surrounding.notes || ''} onChange={event => { setEditSurrounding('infoText', event.target.value); setEditSurrounding('notes', event.target.value); }} /></label></div> : null}
            <div><button onClick={() => setEditOpen(false)}>Cancel</button><button onClick={saveEditor}>Save details</button></div>
          </div> : null}
          {!aiSession.token ? <p className="connect-hint"><Bot /> Sign in from Profile to classify photos and read useful information from captured records.</p> : null}
        </section>

        {isSurroundingItem && item.pendingAiSuggestion ? <section className="pending-ai-review" aria-labelledby="pending-ai-title"><div><span>AI assist</span><h2 id="pending-ai-title">Review before applying.</h2></div><p><strong>{item.pendingAiSuggestion.label || 'Suggested record'}</strong> · {item.pendingAiSuggestion.surrounding?.branch || 'surrounding'} · {item.pendingAiSuggestion.surrounding?.kind || 'object'}</p>{item.pendingAiSuggestion.surrounding?.nextAction ? <p>Suggested next: {item.pendingAiSuggestion.surrounding.nextAction}</p> : null}{item.pendingAiSuggestion.surrounding?.wateringEveryDays ? <p>Suggested watering: every {item.pendingAiSuggestion.surrounding.wateringEveryDays} days.</p> : null}{item.pendingAiSuggestion.surrounding?.branch === 'garden' ? <label><span>Last known watered</span><input type="date" value={pendingWaterDate} onChange={event => setPendingWaterDate(event.target.value)} /></label> : null}<div><button onClick={keepManualDetails}>Keep manual</button><button className="pending-ai-approve" onClick={approvePendingAi}><Check /> Approve AI details</button></div></section> : null}

        {isSurroundingItem ? <section className="surrounding-topic-section" aria-labelledby="surrounding-topic-title"><div><span>{surroundingTopic.eyebrow}</span><h2 id="surrounding-topic-title">{surroundingTopic.title}</h2></div><div className="surrounding-topic-grid">{surroundingTopic.rows.map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}</div><p>{surroundingTopic.note}</p></section> : null}

        {isSurroundingItem ? <>
          <section className="resale-section surrounding-resale-section">
            <h2>Potential sell value</h2>
            <div className="resale-summary"><div><strong>{estimate}</strong><span>{market.updatedAt ? 'AI-estimated local resale range' : 'Scan to estimate this record'}</span></div><div><DollarSign /><span>Best fit</span><strong>{recommendedPlatform}</strong></div></div>
            {market.conditionSummary ? <p className="condition-note">{market.conditionSummary}</p> : null}
            <p className="estimate-disclaimer">{market.disclaimer || 'Estimates are directional. Search similar listings below before choosing a price.'}</p>
            <div className="market-links"><span>Search similar listings</span>{links.map(link => <a href={link.href} target="_blank" rel="noreferrer" key={link.name}>{link.name}<ExternalLink /></a>)}</div>
          </section>
          <section className="resale-section surrounding-info-section">
            <h2>Helpful record</h2>
            <p>{item.surrounding?.infoText || item.surrounding?.notes || 'Read the label, capture what matters, and let this record grow with the object.'}</p>
            {item.surrounding?.care ? <p className="condition-note">{item.surrounding.care}</p> : null}
            {item.surrounding?.nextAction ? <p className="condition-note">Next: {item.surrounding.nextAction}</p> : null}
          </section>
        </> : <section className="resale-section">
          <h2>Resale outlook</h2>
          <div className="resale-summary"><div><strong>{estimate}</strong><span>AI-estimated range</span></div><div><DollarSign /><span>Best fit</span><strong>{recommendedPlatform}</strong></div></div>
          {market.conditionSummary ? <p className="condition-note">{market.conditionSummary}</p> : null}
          <p className="estimate-disclaimer">{market.disclaimer || <>Estimate only — <MirrorWord /> does not yet verify live marketplace matches.</>}</p>
          <div className="market-links"><span>Search current asking listings</span>{links.map(link => <a href={link.href} target="_blank" rel="noreferrer" key={link.name}>{link.name}<ExternalLink /></a>)}</div>
        </section>}

        <section className="move-section">
          <button className="move-toggle" onClick={() => setMoveOpen(value => !value)}><Move /><span><strong>Move item</strong><small>Choose Space → Closet → Section</small></span><ChevronDown /></button>
          {moveOpen ? <div className="move-fields">
            <label className="move-scope-toggle"><input type="checkbox" checked={movingToSurroundings} onChange={event => { const next = event.target.checked; setIncludeSurroundingDestinations(next); setDestinationCollectionId(next && surroundingCollection ? surroundingCollection.id : defaultDestinationCollectionId); setDestinationLocationId(''); setDestinationZoneId(''); }} /><span><strong>Include Surroundings</strong><small>Move this record into a genre, then a station or room.</small></span></label>
            <label><span>Space</span><select value={destinationCollection?.id || ''} onChange={event => { const nextCollection = state.collections.find(collection => collection.id === event.target.value); setDestinationCollectionId(event.target.value); setIncludeSurroundingDestinations(nextCollection?.vibe === 'surrounding'); setDestinationLocationId(''); setDestinationZoneId(''); }}>{(state.collections || []).map(collection => <option value={collection.id} key={collection.id}>{collection.vibe === 'surrounding' ? 'Surroundings' : collection.name}</option>)}</select></label>
            {movingToSurroundings ? <label className="move-genre-field"><span>Genre</span><select value={destinationSurroundingBranch} onChange={event => { setDestinationSurroundingBranch(event.target.value); setDestinationLocationId(''); setDestinationZoneId(''); }}>{SURROUNDING_BRANCHES.map(branch => <option value={branch.id} key={branch.id}>{branch.label}</option>)}</select><small>Default: {defaultSurroundingBranch === destinationSurroundingBranch ? 'your item or profile preference' : 'custom for this move'}</small></label> : null}
            {destinations.length ? <>
              <label><span>{movingToSurroundings ? 'Station / room' : 'Closet'}</span><select value={destinationLocation?.id || ''} onChange={event => { setDestinationLocationId(event.target.value); setDestinationZoneId(''); }}>{destinations.map(location => <option value={location.id} key={location.id}>{location.name}{movingToSurroundings && location.focusBranch ? ` · ${SURROUNDING_BRANCHES.find(branch => branch.id === location.focusBranch)?.shortLabel || location.focusBranch}` : ` · ${location.purpose || 'home'}`}</option>)}</select></label>
              <label><span>Section</span><select value={destinationZone?.id || ''} onChange={event => setDestinationZoneId(event.target.value)}>{(destinationLocation?.zones || []).map(zone => <option value={zone.id} key={zone.id}>{zone.name}</option>)}</select></label>
              <button className="confirm-move" onClick={moveItem}>Move here <ArrowRight /></button>
            </> : <div className="empty-move-space"><p><strong>{movingToSurroundings ? 'No station for this genre yet.' : 'No destination sections here yet.'}</strong><br />{movingToSurroundings ? 'Create a light station, then place the record inside its section.' : 'Create a starter section, review it, then move this item explicitly.'}</p><button className="confirm-move" onClick={movingToSurroundings ? createSurroundingStationAndMove : createClosetAndMove}><Plus /> {movingToSurroundings ? 'Create station + choose it' : 'Create closet + choose it'}</button></div>}
          </div> : null}
        </section>

        <section className="remove-section">
          <button className="remove-toggle" onClick={() => setRemoveOpen(value => !value)} aria-expanded={removeOpen}><Archive /><span><strong>Remove from closet</strong><small>For items sold, donated, or thrown out</small></span><ChevronDown /></button>
          {removeOpen ? <div className="remove-actions" role="group" aria-label="Remove item reason">
            <button onClick={() => removeItem('sold')}>Sold</button>
            <button onClick={() => removeItem('donated')}>Donated</button>
            <button onClick={() => removeItem('thrown out')}>Thrown out</button>
          </div> : null}
        </section>
        {itemFits.length ? <section className="item-insights-section" aria-labelledby="item-insights-title">
          <div className="detail-section-heading"><div><span>Personal record</span><h2 id="item-insights-title">Item insights</h2></div><Sparkles /></div>
          <div className="item-insight-stats"><div><strong>{itemFits.length}</strong><span>saved fit{itemFits.length === 1 ? '' : 's'}</span></div><div><strong>{lastWornLabel}</strong><span>last used</span></div><div><strong>{topCompanionItem?.label || '—'}</strong><span>most paired piece</span></div></div>
          <div className="recent-fits-section"><div className="detail-section-heading"><h2>Recently used</h2><Sparkles /></div>
          <div className="recent-fits-list">{itemFits.slice(0, 5).map(history => {
            const usedItems = flattenState(state).filter(candidate => (history.itemIds || []).map(String).includes(String(candidate.id)));
            return <article key={history.id}>
              <button className="recent-fit-primary" onClick={() => onRecreateOotd?.(history.itemIds)}>
                <PrivateImage source={history.photoData} mediaId={history.photoMediaId} fallback={item.imageData || './assets/silver-knit.webp'} alt={history.name || 'Recent fit'} />
                <span><strong>{history.name || "Today's fit"}</strong><small>{history.itemIds?.length || 0} pieces · recreate this fit</small></span>
              </button>
              <div className="recent-fit-items" aria-label="Pieces used in this fit">
                {usedItems.map(usedItem => <button className={`recent-fit-item ${String(usedItem.id) === String(item.id) ? 'current' : ''}`} key={`${history.id}-${usedItem.id}`} onClick={() => onOpenItem?.(usedItem)}>{usedItem.label || 'Untitled piece'}</button>)}
              </div>
            </article>;
          })}</div></div>
        </section> : null}
          </div>
        </div>
      </main>
    </div>
  );
}

function BottomNav({ view, navigate, surroundingBranch, studioMode, openActions }) {
  const [menuOpen, setMenuOpen] = useState('');
  const closetActive = ['closet', 'public-mirror', 'field', 'intake', 'looks', 'ootd', 'insights'].includes(view);
  const choose = (target, branch) => {
    setMenuOpen('');
    navigate(target, branch);
  };
  return <>
    {menuOpen ? <div className="mobile-nav-drawer-backdrop" role="presentation" onMouseDown={event => event.target === event.currentTarget && setMenuOpen('')}><section className="mobile-nav-drawer" role="dialog" aria-modal="true" aria-label={`${menuOpen === 'closet' ? 'Closet' : 'Surroundings'} navigation`}><div className="mobile-nav-drawer-heading"><strong>{menuOpen === 'closet' ? 'Closet' : 'Surroundings'}</strong><button type="button" onClick={() => setMenuOpen('')} aria-label="Close navigation"><X /></button></div>{menuOpen === 'closet' ? <div className="mobile-nav-drawer-list"><button type="button" className={view === 'closet' ? 'active' : ''} onClick={() => choose('closet')}><Shirt /><span><strong>Closet home</strong><small>Spaces, closets, and clothing</small></span><ChevronRight /></button>{MOBILE_CLOSET_NAV.map(item => { const Icon = item.icon; const active = item.id === 'ootd' ? view === 'looks' && studioMode === 'ootd' : item.id === 'looks' ? view === 'looks' && studioMode !== 'ootd' : view === item.id; return <button type="button" className={active ? 'active' : ''} onClick={() => choose(item.id)} key={item.id}><Icon /><span><strong>{item.label}</strong><small>{item.id === 'field' ? 'Relationships and pairings' : item.id === 'intake' ? 'Place new captures' : item.id === 'insights' ? 'Patterns over time' : 'Build and remember fits'}</small></span><ChevronRight /></button>; })}</div> : <div className="mobile-nav-drawer-list"><button type="button" className={surroundingBranch === 'all' ? 'active' : ''} onClick={() => choose('surrounding', 'all')}><Boxes /><span><strong>Everything</strong><small>Rooms, objects, and care</small></span><ChevronRight /></button>{SURROUNDING_BRANCHES.map(branch => <button type="button" className={surroundingBranch === branch.id ? 'active' : ''} onClick={() => choose('surrounding', branch.id)} key={branch.id}><BookOpen /><span><strong>{branch.shortLabel}</strong><small>{branch.label}</small></span><ChevronRight /></button>)}</div>}</section></div> : null}
    <nav className="bottom-nav condensed" aria-label="Primary navigation">{NAV.map(item => { const Icon = item.icon; const active = item.id === 'closet' ? closetActive : view === item.id; const opensMenu = item.id === 'closet'; return <button key={item.id} className={`${active ? 'active' : ''} ${item.primary ? 'primary' : ''}`.trim()} aria-expanded={opensMenu ? menuOpen === item.id : undefined} aria-label={item.primary ? 'Open quick actions' : undefined} onClick={() => item.primary ? openActions?.() : opensMenu ? setMenuOpen(current => current === item.id ? '' : item.id) : choose(item.id)}><span><Icon /></span><small>{item.label}</small></button>; })}</nav>
  </>;
}

function ScanQueueStatus({ active, pending }) {
  if (!active && !pending) return null;
  const message = active ? 'AI scan in progress' : 'AI scans waiting';
  const detail = pending ? `${pending} ${pending === 1 ? 'piece' : 'pieces'} next` : 'Next item starts automatically';
  return <div className="scan-queue-status" role="status" aria-live="polite"><ScanLine /><span><strong>{message}</strong><small>{detail}</small></span></div>;
}

export default function App() {
  const requestedView = new URLSearchParams(location.search).get('view') || 'today';
  const requestedAction = new URLSearchParams(location.search).get('action') || '';
  const requestedDesktopPanel = ['field', 'intake', 'closet', 'public-mirror', 'thrift', 'looks', 'ootd', 'schedule', 'insights', 'profile', 'surrounding', 'guides'].includes(requestedView) ? requestedView : 'dashboard';
  const [ready, setReady] = useState(false);
  const [state, setState] = useState(null);
  const [view, setView] = useState(requestedView);
  const [closetFilter, setClosetFilter] = useState('all');
  const [captureOpen, setCaptureOpen] = useState(requestedAction === 'capture' && requestedView !== 'surrounding');
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const [importHubOpen, setImportHubOpen] = useState(false);
  const [guideAuthorMode, setGuideAuthorMode] = useState(false);
  const [surroundingCaptureOpen, setSurroundingCaptureOpen] = useState(requestedAction === 'capture' && requestedView === 'surrounding');
  const [selectedItemRef, setSelectedItemRef] = useState(null);
  const [tryOnSeedItemIds, setTryOnSeedItemIds] = useState([]);
  const [ootdSeedItemIds, setOotdSeedItemIds] = useState([]);
  const [studioMode, setStudioMode] = useState('tryon');
  const [surroundingBranch, setSurroundingBranch] = useState('all');
  const [adminPanel, setAdminPanel] = useState('activity');
  const [desktopPanel, setDesktopPanel] = useState(requestedDesktopPanel);
  const [aiSession, setAiSession] = useState(() => getAiSession());
  const [scanningItems, setScanningItems] = useState(() => new Set());
  const [scanQueueState, setScanQueueState] = useState({ active: false, pending: 0 });
  const [toast, setToast] = useState('');
  const cooldownState = useRateLimitCooldown();
  const importRef = useRef(null);
  const toastTimerRef = useRef(null);
  const scanQueueRef = useRef({ jobs: [], keys: new Set(), activeKey: '', running: false });
  const desktopLayout = useMediaQuery('(min-width: 768px)');

  useEffect(() => {
    const referral = new URLSearchParams(location.search).get('ref') || '';
    if (/^MIR-[A-Z0-9_-]{6,32}$/i.test(referral)) {
      try { localStorage.setItem('mirror_referral_code', referral.toUpperCase()); } catch { /* Referral attribution is optional. */ }
    }
  }, []);

  const refresh = useCallback(() => setState(Store.getState()), []);
  const openView = useCallback(nextView => {
    if (nextView === 'closet') setClosetFilter('all');
    if (nextView === 'looks') setStudioMode('tryon');
    setView(nextView);
  }, []);
  const navigateMobile = useCallback((nextView, branch = 'all') => {
    if (nextView === 'ootd') {
      setStudioMode('ootd');
      setView('looks');
      return;
    }
    if (nextView === 'looks') setStudioMode('tryon');
    if (nextView === 'closet') setClosetFilter('all');
    if (nextView === 'surrounding') setSurroundingBranch(branch);
    setView(nextView === 'dashboard' ? 'today' : nextView);
  }, []);
  const openDesktopPanel = useCallback(nextPanel => {
    setDesktopPanel(nextPanel);
    if (nextPanel === 'dashboard') setView('today');
    else if (nextPanel === 'intake') setView('closet');
    else setView(nextPanel);
  }, []);
  const openGuides = useCallback((author = false) => {
    const isAjp = String(aiSession.user?.username || '').toLowerCase() === 'ajp';
    setGuideAuthorMode(!!author && isAjp);
    if (desktopLayout) setDesktopPanel('guides');
    setView('guides');
  }, [aiSession.user?.username, desktopLayout]);
  const openDesktopSurroundingBranch = useCallback(branch => {
    setSurroundingBranch(branch);
    setDesktopPanel('surrounding');
    setView('surrounding');
  }, []);
  const openSurroundingCapture = useCallback(branch => {
    setSurroundingBranch(branch || 'all');
    setView('surrounding');
    if (desktopLayout) {
      setDesktopPanel('surrounding');
      return;
    }
    setSurroundingCaptureOpen(true);
  }, [desktopLayout]);
  const navigateFromActions = useCallback(target => {
    if (desktopLayout) {
      openDesktopPanel(target === 'today' ? 'dashboard' : target);
      return;
    }
    navigateMobile(target);
  }, [desktopLayout, navigateMobile, openDesktopPanel]);
  const openAdmin = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    setAdminPanel('activity');
    setDesktopPanel('admin');
    setView('admin');
  }, []);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let session = getAiSession();
      if (session.token) {
        try {
          session = await refreshMirrorSession();
        } catch {
          session = getAiSession().token ? lockAiSessionIdentity() : getAiSession();
        }
      }
      if (cancelled) return;
      setAiSession(session);
      await Store.init();
      if (cancelled) return;
      const current = Store.getState();
      const currentHasData = hasWardrobeData(current);
      if (session.user?.id && !currentHasData && await Store.hasGuestState()) {
        const claim = window.confirm(`Move this browser's existing MIЯЯOЯ closet into ${session.user.displayName || session.user.username}? A safety copy will be kept.`);
        if (claim) {
          await Store.claimGuestStateForUser(session.user.id);
          window.location.reload();
          return;
        }
      }
      refresh();
      setReady(true);
      if (!new URLSearchParams(location.search).get('view') && current.preferences?.defaultToSurroundings) {
        setSurroundingBranch(current.preferences?.defaultSurroundingBranch || 'all');
        setView('surrounding');
        setDesktopPanel('surrounding');
      }
      if (new URLSearchParams(location.search).get('action') === 'capture') setCaptureOpen(true);
    })();
    return () => { cancelled = true; };
  }, [refresh]);

  useEffect(() => {
    const unlockAfterReconnect = () => {
      if (!aiSession.token && getAiSession().token) window.location.reload();
    };
    window.addEventListener('online', unlockAfterReconnect);
    return () => window.removeEventListener('online', unlockAfterReconnect);
  }, [aiSession.token]);

  useEffect(() => {
    if (!ready || !aiSession.token || (view === 'admin' && aiSession.user?.role !== 'admin')) return;
    void recordMirrorActivity(view);
  }, [aiSession.token, aiSession.user?.role, ready, view]);

  useEffect(() => {
    const handler = event => setToast(event.detail?.message || 'MIЯЯOЯ could not save this change.');
    window.addEventListener('thecloset:persist-error', handler);
    return () => window.removeEventListener('thecloset:persist-error', handler);
  }, []);

  useEffect(() => {
    const reloadForProfileChange = () => window.location.reload();
    const storageHandler = event => {
      if (event.key === PROFILE_SIGNAL_KEY) reloadForProfileChange();
    };
    window.addEventListener('storage', storageHandler);
    const channel = typeof BroadcastChannel === 'function' ? new BroadcastChannel('mirror-profile') : null;
    if (channel) channel.onmessage = reloadForProfileChange;
    return () => {
      window.removeEventListener('storage', storageHandler);
      channel?.close();
    };
  }, []);

  const showToast = useCallback(message => {
    setToast(message);
    window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(''), 3600);
  }, []);
  const { sync, conflict, resolveConflict, abortSync, resumeSync, clearForProfileChange } = useMirrorSync({
    ready,
    session: aiSession,
    onState: setState,
    onSession: setAiSession,
    showToast,
  });
  const handleStudioCrash = useCallback(() => {
    setView('today');
    showToast('Looks had trouble loading. We returned you to Today.');
  }, [showToast]);
  const signedIn = async session => {
    const profileId = Number(session.user?.id);
    if (profileId > 0 && !(await Store.hasProfileState(profileId)) && hasWardrobeData(Store.getState())) {
      const moveCurrent = window.confirm(`Move this browser's current MIЯЯOЯ closet into ${session.user.displayName || session.user.username}? A safety copy will be kept.`);
      if (moveCurrent) await Store.claimCurrentStateForUser(profileId, { clearGuest: true });
    }
    clearForProfileChange();
    setAiSession(session);
    announceProfileChange();
    window.location.reload();
  };
  const profileSelected = async session => {
    clearForProfileChange();
    setAiSession(session);
    announceProfileChange();
    window.location.reload();
  };
  const signedOut = () => {
    clearForProfileChange();
    clearPrivateMediaCache();
    clearAiSession();
    setAiSession(getAiSession());
    announceProfileChange();
    window.location.reload();
  };
  const importBackup = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const imported = await readMirrorBackup(file);
      if (flattenState(state).length && !window.confirm('Replace the local data on this browser with this MIЯЯOЯ backup?')) return;
      Store.replaceState(imported);
      refresh();
      showToast('Your MIЯЯOЯ data is here. Nothing left behind.');
      setView('today');
    } catch (error) {
      showToast(error.message || 'That backup could not be imported.');
    }
  };
  const openItem = entry => setSelectedItemRef({ locationId: entry.location.id, zoneId: entry.zone.id, itemId: entry.id });
  const openTryOn = entry => {
    setTryOnSeedItemIds([entry.id]);
    setStudioMode('tryon');
    setSelectedItemRef(null);
    setView('looks');
  };
  const openOotd = useCallback(itemIds => {
    setOotdSeedItemIds((itemIds || []).map(String));
    setStudioMode('ootd');
    setSelectedItemRef(null);
    setView('looks');
  }, []);
  const openFieldTryOn = useCallback(itemIds => {
    setTryOnSeedItemIds((itemIds || []).map(String).slice(0, 4));
    setStudioMode('tryon');
    setDesktopPanel('looks');
    setView('looks');
  }, []);
  const saveFieldOotd = useCallback(itemIds => {
    const ids = [...new Set((itemIds || []).map(String))];
    if (ids.length < 2) return;
    Store.saveOotdHistory({
      name: `Field thought · ${new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`,
      itemIds: ids,
      note: 'Built in MIЯЯOЯ Field',
    });
    refresh();
    showToast('Thought saved to OoTD history.');
  }, [refresh, showToast]);
  const syncEntryPhotos = useCallback(async entryOrReference => {
    const reference = entryOrReference.location
      ? { locationId: entryOrReference.location.id, zoneId: entryOrReference.zone.id, itemId: entryOrReference.id }
      : entryOrReference;
    const liveEntry = findItemEntry(Store.getState(), reference);
    if (!liveEntry || !getAiSession().token) return;
    Store.updateItem(reference.locationId, reference.zoneId, reference.itemId, { serverSyncStatus: 'syncing', serverSyncError: '' });
    refresh();
    try {
      const itemUpload = await uploadMirrorImage(liveEntry.imageData, 'item');
      let backUpload = null;
      if (liveEntry.backImageData) backUpload = await uploadMirrorImage(liveEntry.backImageData, 'item');
      let tagUpload = null;
      if (liveEntry.tagImageData) tagUpload = await uploadMirrorImage(liveEntry.tagImageData, 'care-label');
      let careUpload = null;
      if (liveEntry.careImageData) careUpload = await uploadMirrorImage(liveEntry.careImageData, 'care-photo');
      Store.updateItem(reference.locationId, reference.zoneId, reference.itemId, {
        serverSyncStatus: 'complete',
        serverSyncError: '',
        imageData: '',
        imageMediaId: itemUpload.mediaId || '',
        imageSha256: itemUpload.sha256 || '',
        backImageData: '',
        backImageMediaId: backUpload?.mediaId || '',
        backImageSha256: backUpload?.sha256 || '',
        tagImageData: '',
        tagImageMediaId: tagUpload?.mediaId || '',
        tagImageSha256: tagUpload?.sha256 || '',
        careImageData: '',
        careImageMediaId: careUpload?.mediaId || '',
        careImageSha256: careUpload?.sha256 || '',
        mediaStatus: 'private',
        serverSyncedAt: new Date().toISOString(),
      });
      setAiSession(getAiSession());
      refresh();
    } catch (error) {
      Store.updateItem(reference.locationId, reference.zoneId, reference.itemId, { serverSyncStatus: 'failed', serverSyncError: error.message || 'VPS photo sync failed.' });
      refresh();
      if (!error.cooldown) showToast(error.message || 'The photo is safe locally, but VPS sync needs another try.');
    }
  }, [refresh]);
  const publishScanQueue = useCallback(() => {
    const queue = scanQueueRef.current;
    setScanQueueState({ active: !!queue.activeKey, pending: queue.jobs.length });
  }, []);
  const drainScanQueue = useCallback(async () => {
    const queue = scanQueueRef.current;
    if (queue.running) return;
    queue.running = true;
    publishScanQueue();
    while (queue.jobs.length) {
      const job = queue.jobs.shift();
      queue.activeKey = job.key;
      publishScanQueue();
      const liveEntry = findItemEntry(Store.getState(), job.reference);
      if (!liveEntry) {
        queue.keys.delete(job.key);
        queue.activeKey = '';
        publishScanQueue();
        continue;
      }
      setScanningItems(current => new Set(current).add(job.key));
      Store.updateItem(job.reference.locationId, job.reference.zoneId, job.reference.itemId, { aiScanStatus: 'scanning', aiScanError: '' });
      refresh();
      try {
        const payload = await scanMirrorItem(liveEntry, job.mode, { autoScan: job.autoScan });
        const aiSuggestion = payload.suggestion || {};
        Store.updateItem(job.reference.locationId, job.reference.zoneId, job.reference.itemId, job.approvalRequired
          ? {
              aiScanStatus: 'complete',
              aiScanError: '',
              aiConfidence: Number(aiSuggestion.confidence || 0),
              aiNotes: 'AI suggestion is waiting for your approval.',
              aiNeedsReview: true,
              pendingAiSuggestion: aiSuggestion,
            }
          : {
              ...mergeAiSuggestion(liveEntry, aiSuggestion),
              aiScanStatus: 'complete',
              aiScanError: '',
            });
        setAiSession(getAiSession());
        refresh();
        showToast(job.approvalRequired
          ? 'AI suggestion ready. Open the record to approve or keep your manual details.'
          : `AI scan complete: ${payload.suggestion?.label || liveEntry.label || 'item'} is ready to review.`);
      } catch (error) {
        Store.updateItem(job.reference.locationId, job.reference.zoneId, job.reference.itemId, { aiScanStatus: 'failed', aiScanError: error.message || 'AI scan failed.' });
        refresh();
        if (!error.cooldown) showToast(error.message || 'AI scan failed. The intake photo is still saved.');
      } finally {
        setScanningItems(current => {
          const next = new Set(current);
          next.delete(job.key);
          return next;
        });
        queue.keys.delete(job.key);
        queue.activeKey = '';
        publishScanQueue();
      }
    }
    queue.running = false;
    publishScanQueue();
  }, [publishScanQueue, refresh, showToast]);
  const scanEntry = useCallback((entryOrReference, mode = 'inventory_and_value_scan', { autoScan = false, approvalRequired = false } = {}) => {
    const reference = entryOrReference.location
      ? { locationId: entryOrReference.location.id, zoneId: entryOrReference.zone.id, itemId: entryOrReference.id }
      : entryOrReference;
    const key = `${reference.locationId}:${reference.zoneId}:${reference.itemId}`;
    if (!findItemEntry(Store.getState(), reference)) return;
    if (!getAiSession().token) {
      showToast('Sign in from Profile to use MIЯЯOЯ intelligence.');
      return;
    }
    const queue = scanQueueRef.current;
    if (queue.keys.has(key)) return;
    queue.keys.add(key);
    queue.jobs.push({ reference, key, mode, autoScan, approvalRequired });
    Store.updateItem(reference.locationId, reference.zoneId, reference.itemId, { aiScanStatus: 'queued', aiScanError: '' });
    refresh();
    publishScanQueue();
    void drainScanQueue();
  }, [drainScanQueue, publishScanQueue, refresh, showToast]);

  const captureSaved = saved => {
    const references = Array.isArray(saved) ? saved : [saved];
    refresh();
    setCaptureOpen(false);
    references.filter(reference => reference.sync).forEach(reference => { void syncEntryPhotos(reference); });
    references.filter(reference => reference.scan).forEach(reference => { void scanEntry(reference, 'inventory_and_value_scan', { autoScan: true }); });
    const count = references.length;
    if (references.some(reference => reference.scan)) {
      showToast(count > 1 ? `${count} pieces captured. MIЯЯOЯ AI queued each one for review.` : 'Captured. MIЯЯOЯ AI is reading the piece now.');
    } else {
      showToast(count > 1 ? `${count} pieces captured to Unsorted Intake.` : 'Captured. Choose Wear, Sell, and a closet whenever you are ready.');
    }
  };
  if (!ready || !state) return <div className="boot"><BrandMark /><span>Opening your closet…</span></div>;
  if (!aiSession.token) return <AuthSplash onSignedIn={signedIn} />;
  if (aiSession.user?.isDemo) return <Suspense fallback={<div className="boot"><BrandMark /><span>Opening the protected demonstration…</span></div>}><DemoExperience state={state} user={aiSession.user} onSignedOut={signedOut} /></Suspense>;
  const items = flattenState(state);
  const isGuideAuthor = String(aiSession.user?.username || '').toLowerCase() === 'ajp';
  const intakeCount = getIntakeItems(state).length;
  const selectedItem = findItemEntry(state, selectedItemRef);
  const selectedScanKey = selectedItemRef ? `${selectedItemRef.locationId}:${selectedItemRef.zoneId}:${selectedItemRef.itemId}` : '';
  const adminContent = aiSession.user?.role === 'admin'
    ? adminPanel === 'accounts'
      ? <AdminAccountsView adminName={aiSession.user?.displayName || aiSession.user?.username || 'Admin'} onBack={() => openDesktopPanel('profile')} onOpenActivity={() => setAdminPanel('activity')} onOpenDevAttention={() => setAdminPanel('dev-attention')} />
      : adminPanel === 'dev-attention'
        ? <AdminDevAttentionView adminName={aiSession.user?.displayName || aiSession.user?.username || 'Admin'} onBack={() => openDesktopPanel('profile')} onOpenActivity={() => setAdminPanel('activity')} onOpenAccounts={() => setAdminPanel('accounts')} />
        : <AdminMetricsView adminName={aiSession.user?.displayName || aiSession.user?.username || 'Admin'} onBack={() => openDesktopPanel('profile')} onOpenAccounts={() => setAdminPanel('accounts')} onOpenDevAttention={() => setAdminPanel('dev-attention')} />
    : null;
  const desktopPanelContent = {
    closet: <ClosetView state={state} refresh={refresh} openCapture={() => setCaptureOpen(true)} onOpenItem={openItem} sync={sync} onAbortSync={abortSync} onResumeSync={resumeSync} initialFilter={closetFilter} user={aiSession.user} />,
    'public-mirror': <ClosetView state={state} refresh={refresh} openCapture={() => setCaptureOpen(true)} onOpenItem={openItem} sync={sync} onAbortSync={abortSync} onResumeSync={resumeSync} initialLocation="public-mirror" user={aiSession.user} />,
    thrift: <ThriftView items={items} onOpenItem={openItem} refresh={refresh} />,
    schedule: <MirroredSchedule state={state} items={items} onOpenItem={openItem} refresh={refresh} aiSession={aiSession} onOpenLooks={openOotd} showToast={showToast} />,
    looks: <RouteErrorBoundary route="looks" onCrash={handleStudioCrash} onRecover={() => openDesktopPanel('dashboard')}><Suspense fallback={<div className="boot"><BrandMark /><span>Opening your private studio…</span></div>}><TryOnStudio state={state} items={items} seedItemIds={tryOnSeedItemIds} ootdSeedItemIds={ootdSeedItemIds} initialMode="tryon" aiSession={aiSession} refresh={refresh} showToast={showToast} onSession={setAiSession} cooldownState={cooldownState} onOpenItem={openItem} /></Suspense></RouteErrorBoundary>,
    ootd: <RouteErrorBoundary route="looks/ootd" onCrash={handleStudioCrash} onRecover={() => openDesktopPanel('dashboard')}><Suspense fallback={<div className="boot"><BrandMark /><span>Opening your private studio…</span></div>}><TryOnStudio state={state} items={items} seedItemIds={tryOnSeedItemIds} ootdSeedItemIds={ootdSeedItemIds} initialMode="ootd" aiSession={aiSession} refresh={refresh} showToast={showToast} onSession={setAiSession} cooldownState={cooldownState} onOpenItem={openItem} /></Suspense></RouteErrorBoundary>,
    profile: <ProfileView state={state} refresh={refresh} onImport={importBackup} importRef={importRef} aiSession={aiSession} onAiSession={setAiSession} onSignedIn={signedIn} onProfileSelected={profileSelected} onSignedOut={signedOut} showToast={showToast} sync={sync} onAbortSync={abortSync} onResumeSync={resumeSync} onOpenAdmin={openAdmin} onOpenGuides={openGuides} onOpenImportHub={() => setImportHubOpen(true)} isGuideAuthor={isGuideAuthor} />,
    guides: <Suspense fallback={<div className="boot"><BrandMark /><span>Opening the field guide…</span></div>}><GuidesView onBack={() => openDesktopPanel('dashboard')} authorMode={guideAuthorMode && isGuideAuthor} /></Suspense>,
    admin: adminContent,
  };
  return (
    <div className="app-shell">
      {desktopLayout ? <DesktopWorkspace panel={desktopPanel} onPanel={openDesktopPanel} onSurroundingBranch={openDesktopSurroundingBranch} surroundingBranch={surroundingBranch} state={state} items={items} user={aiSession.user} aiSession={aiSession} sync={sync} refresh={refresh} showToast={showToast} openCapture={() => setCaptureOpen(true)} openQuickActions={() => setQuickActionsOpen(true)} onOpenItem={openItem} onOpenFieldTryOn={openFieldTryOn} onSaveFieldOotd={saveFieldOotd} onScan={scanEntry} panelContent={desktopPanelContent} /> : <>
        {view === 'today' || (view === 'admin' && aiSession.user?.role !== 'admin') ? <ReflectiveToday state={state} items={items} user={aiSession.user} sync={sync} openCapture={() => setCaptureOpen(true)} onOpenPanel={navigateMobile} onOpenItem={openItem} refresh={refresh} /> : null}
        {view === 'closet' ? <ClosetView state={state} refresh={refresh} openCapture={() => setCaptureOpen(true)} onOpenItem={openItem} sync={sync} onAbortSync={abortSync} onResumeSync={resumeSync} initialFilter={closetFilter} user={aiSession.user} /> : null}
        {view === 'public-mirror' ? <ClosetView state={state} refresh={refresh} openCapture={() => setCaptureOpen(true)} onOpenItem={openItem} sync={sync} onAbortSync={abortSync} onResumeSync={resumeSync} initialLocation="public-mirror" user={aiSession.user} /> : null}
        {view === 'intake' ? <main className="view mobile-intake-page"><div className="mobile-workspace-heading"><span>Closet</span><h1>Unsorted Intake</h1><p>Search, select, and place what your phone captured.</p></div><DesktopIntakeModule items={items} state={state} refresh={refresh} openCapture={() => setCaptureOpen(true)} onOpenItem={openItem} /></main> : null}
        {view === 'field' ? <MirrorField state={state} items={items} openCapture={() => setCaptureOpen(true)} onOpenTryOn={openFieldTryOn} onSaveOotd={saveFieldOotd} onOpenItem={openItem} /> : null}
        {view === 'insights' ? <div className="mobile-insights-wrap"><DesktopInsightsView state={state} items={items} /></div> : null}
        {view === 'thrift' ? <ThriftView items={items} onOpenItem={openItem} refresh={refresh} /> : null}
        {view === 'surrounding' ? <SurroundingView state={state} items={items} refresh={refresh} onOpenItem={openItem} onBack={() => setView('today')} aiSession={aiSession} onScan={scanEntry} showToast={showToast} captureOpen={surroundingCaptureOpen} onCaptureClose={() => setSurroundingCaptureOpen(false)} initialBranch={surroundingBranch} /> : null}
        {view === 'looks' ? <RouteErrorBoundary route="looks" onCrash={handleStudioCrash} onRecover={() => setView('today')}><Suspense fallback={<div className="boot"><BrandMark /><span>Opening your private studio…</span></div>}><TryOnStudio state={state} items={items} seedItemIds={tryOnSeedItemIds} ootdSeedItemIds={ootdSeedItemIds} initialMode={studioMode} aiSession={aiSession} refresh={refresh} showToast={showToast} onSession={setAiSession} cooldownState={cooldownState} onOpenItem={openItem} /></Suspense></RouteErrorBoundary> : null}
        {view === 'schedule' ? <MirroredSchedule state={state} items={items} onOpenItem={openItem} refresh={refresh} aiSession={aiSession} onOpenLooks={openOotd} showToast={showToast} /> : null}
        {view === 'admin' ? adminContent : null}
        {view === 'profile' ? desktopPanelContent.profile : null}
        {view === 'guides' ? <Suspense fallback={<div className="boot"><BrandMark /><span>Opening the field guide…</span></div>}><GuidesView onBack={() => setView('today')} authorMode={guideAuthorMode && isGuideAuthor} /></Suspense> : null}
      </>}
      {!desktopLayout ? <MirrorFooter /> : null}
      <ScanQueueStatus active={scanQueueState.active} pending={scanQueueState.pending} />
      {!desktopLayout ? <BottomNav view={view} navigate={navigateMobile} surroundingBranch={surroundingBranch} studioMode={studioMode} openActions={() => setQuickActionsOpen(true)} /> : null}
      <CaptureSheet open={captureOpen} onClose={() => setCaptureOpen(false)} onSaved={captureSaved} aiSession={aiSession} />
      <QuickActions open={quickActionsOpen} onClose={() => setQuickActionsOpen(false)} onCapture={() => setCaptureOpen(true)} onNavigate={navigateFromActions} onSurroundingCapture={openSurroundingCapture} />
      {importHubOpen ? <Suspense fallback={null}><ImportHub open={importHubOpen} state={state} refresh={refresh} onClose={() => setImportHubOpen(false)} showToast={showToast} /></Suspense> : null}
      <ItemDetailSheet entry={selectedItem} state={state} onClose={() => setSelectedItemRef(null)} refresh={refresh} aiSession={aiSession} onScan={scanEntry} scanning={scanningItems.has(selectedScanKey)} onTryOn={openTryOn} cooldownState={cooldownState} showToast={showToast} onRecreateOotd={openOotd} onOpenItem={openItem} />
      <SyncConflict conflict={conflict} onResolve={resolveConflict} onCancel={abortSync} />
      <RateLimitNotice state={cooldownState} />
      {toast ? <div className="toast" role="status">{toast}</div> : null}
    </div>
  );
}
